import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../translations';
import InventoryModal from '../../../components/InventoryModal';
import { AmountField, DiscountField, AmountNote } from '../../../ui/AmountField';
import {
  amountOrZero, amountToRaw, parseAmount, discountInPln, discountError, advanceError,
  amountErrorMessage, normalizeDiscountUnit, capAmountsToPrice, roundToCents,
} from '../../../utils/amountField';
import useAmountChecks from '../../../utils/useAmountChecks';
import { PrimaryButton, SecondaryButton } from '../../../ui/common';
import { useToast } from '../../../components/ui/Toast';
import { useConfirm } from '../../../components/ui/Modal';
import { API_BASE } from '../../../config';
import { makeThumbnail, prepareUploadPhoto } from '../../../utils/imageThumb';
import { wyslijPoKolei, pozostaleWFormularzu, komunikatSzczegolow } from '../../../utils/wysylkaDokumentacji';
import { buildContractDataFromCalendar, sendAndDownloadContract } from '../../../utils/contract';
import DatePicker from 'react-datepicker';
import { shift } from '@floating-ui/react';
import {
  CalendarIcon, PlusIcon, AlertTriangleIcon, DocumentIcon, TrashIcon,
  UserIcon, PhoneIcon, MailIcon, MapPinIcon, FileTextIcon, PackageIcon,
  TagIcon, CreditCardIcon,
} from '../icons';
import {
  CloseButton, EmptyState, FormInput, FormTextarea,
  EquipmentInfo, EquipmentName, EquipmentMeta, EquipmentPrice, LoadingSpinner,
  RangeLabelRow, ClearRangeButton, DatepickerTheme, DatepickerZIndexFix,
  EquipmentItemRow, RemoveButton, EquipmentButton, FormModal, FormContainer,
  FormHeader, FormTitle, EquipmentActions, FormSubtitle, FormPanel,
  FormPanelHeader, FormPanelIcon, FormPanelTitle, FormInputGroup, ContactGrid,
  FormInputWrapper, FormLabel, FormValue, CompactGrid,
  FormEquipmentList, SummaryTable, SummaryRow, SummaryLabel, SummaryValue,
  DocFileButton, DocEntry, DocRemove,
} from '../calendar.styles';
import SimpleSelect from '../../../ui/SimpleSelect';
import { formatYMD, parseYMD, getStartOfToday, daysInclusive, formatConflictDate, getEquipmentDailyRate, RESERVATION_STATUS_CODES, STATUS_STYLES, reservationStatusCode } from '../utils';
import { fetchPriceQuote, fetchClassRequestQuote, quoteKey } from '../../../utils/priceQuote';
import { dniPlatne, dniPlatneZWyceny, pokazDniPlatne } from '../../../utils/dniPlatne';
import { useTabelaDniPlatnych } from '../../../utils/useTabelaDniPlatnych';
import { buildPriceBreakdown, groupEquipmentForDisplay } from '../../../utils/priceBreakdown';
import { ItemRate, RuleBadges, BundleFrame, QuotePendingNote, SummaryBreakdownRows } from '../../../components/booking/PriceBreakdown';
import CustomRangeInput from './CustomRangeInput';
import AddressModal from './AddressModal';
import HandoverPanel from '../../../components/handover/HandoverPanel';
import ClassRequestPanel from '../../../components/classBooking/ClassRequestPanel';
import { Banner } from '../../../components/ui/Banner';
import {
  DetailsHero, DetailsEyebrow, DetailsTitleRow, DetailsTitle, StatusPill, DetailsWhen, ContactRow, ContactChip,
  DetailsGrid, DetailsColumn, DetailsCard, DetailsCardHead, DetailsCardTitle, DetailsCount, PaymentGroup, NoteText,
  MoneyCard, MoneyLabel, MoneyAmount, MoneyNote, StickyActions,
} from './reservationDetails.styles';
import { formatMoney, formatDate, localeTag } from '../../../utils/format';
import { maPremium } from '../../../utils/planFirmy';
import PremiumNotice from '../../../components/premium/PremiumNotice';
import { usePremiumBlad } from '../../../components/premium/PremiumUpgrade';
import PaymentDeadlineField, { useTerminWplaty } from '../../../components/payments/PaymentDeadlineField';
import PaymentDeadlineInfo from '../../../components/payments/PaymentDeadlineInfo';
import { godzinyPoPrzypomnieniuWidgetu } from '../../../utils/terminWplaty';
import { nazwaMetody, slowoMetody } from '../../../utils/metodaWplaty';
import { TylkoOdczytWPrzerwie } from '../../../components/przerwa/KontoWPrzerwie';

// Kalendarzyk dat dosuwany do krawędzi ekranu. Na dotyku dni mają 44 px, więc kalendarzyk jest
// szerszy (index.css), a react-datepicker 8 sam go tylko odwraca (flip), nie przesuwa: na telefonie
// 390 px strzałka następnego miesiąca i niedziela chowały się za krawędzią (przegląd gałęzi, 28.09.2026).
const DOSUN_DO_EKRANU = [shift({ padding: 8 })];

const CameraIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

// Kopiowanie do schowka odporne na Safari: po `await fetch` gest użytkownika wygasa
// i navigator.clipboard rzuca NotAllowedError. Fallback: textarea+execCommand, potem prompt.
// Zwraca `true` TYLKO gdy link naprawdę trafił do schowka. Ostatnia deska ratunku (okienko
// z tekstem do ręcznego skopiowania) zwracała `true` tak samo jak sukces, więc panel meldował
// „Link skopiowany" również wtedy, gdy okienko zamknięto bez kopiowania. Piotrek stracił tak
// link do raty 2026-08-04: schowek odmówił, okienko poszło zamknąć, komunikat powiedział, że
// wszystko poszło dobrze, a w schowku nie było nic. Przy linku do PŁATNOŚCI to najgorszy
// możliwy moment na fałszywe potwierdzenie, bo pracownik odsyła klienta z niczym.
async function copyTextToClipboard(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch (_) { /* fallback */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    if (ok) return true;
  } catch (_) { /* fallback */ }
  // Okienko pokazuje link i tak, więc pracownik ma co skopiować. Nie wiemy jednak, czy to
  // zrobił, i właśnie tego nie wolno zgadywać na korzyść.
  window.prompt('Skopiuj link (Cmd+C, Enter):', text);
  return false;
}



// Slad po ostatniej wysylce linku. Dane sa w bazie od P3.21 (zdarzenia link_sent/link_copied),
// ale panel ich nie pokazywal — obsluga widziala tylko przyciski i nie wiedziala, czy prosba juz
// poszla. Przy dwoch osobach przy ladzie konczy sie to albo podwojna wysylka, albo brakiem
// wysylki, bo kazdy zaklada, ze zrobil to ktos inny. Zwrocil na to uwage Piotrek 2026-08-06.
//
// CELOWO nie blokujemy ponownej wysylki: bywa potrzebna (klient zgubil maila, zmienil adres).
// To ma byc informacja, nie bramka.
// Odnotowanie skopiowania linku. Wolane WYLACZNIE po UDANYM zapisie do schowka, bo backend
// w chwili generowania linku jeszcze nie wie, czy sie uda. Best-effort: nieodnotowany slad jest
// drobiazgiem, nieudane kopiowanie z falszywym potwierdzeniem juz nie.
async function potwierdzSkopiowanie(paymentId) {
  if (!paymentId) return;
  try {
    await fetch(`${API_BASE}/api/payments/${paymentId}/link-copied`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'x-csrf-token': localStorage.getItem('csrfToken') || '' },
    });
  } catch (_) { /* panel dziala dalej, slad po prostu nie powstanie */ }
}

function SladWysylki({ platnosc }) {
  if (!platnosc || !platnosc.link_last_at) return null;
  const kiedy = new Date(platnosc.link_last_at);
  // Osloną warto trzymac, odkad wartosc moze przyjsc NIE z kolumny znacznika czasu, tylko
  // z `metadata` jako tekst (P3.29). Kolumna byla NOT NULL i osloną bylaby martwa; pole jsonb
  // takiej gwarancji nie daje, a zla data wywalilaby caly panel rezerwacji.
  if (Number.isNaN(kiedy.getTime())) return null;
  const skopiowany = platnosc.link_last_kind === 'link_copied';
  return (
    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 6 }}>
      {skopiowany ? 'Link skopiowany' : 'Link wysłany'}
      {' '}
      {kiedy.toLocaleString('pl-PL', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })}
    </div>
  );
}

// Kwota podpowiadana w polu albo w oknie z kwotą: przecinek jak w polach kwot, bez spacji
// tysięcy, bo pola i okna czytają ją z powrotem (utils/amountField.js#parseAmount, zamiana
// przecinka na kropkę). Test na iPadzie, 18.10.2026: podpowiedzi miały kropkę.
const kwotaDoPola = (v) => (Math.round((Number(v) || 0) * 100) / 100).toFixed(2).replace('.', ',');

// Stan przycisku wysylki albo kopiowania linku, ktory nie dotyczy zadnej sekcji platnosci.
const BEZ_STANU_PRZYCISKU = { sekcja: null, stan: null };

// Czy wybrany sprzęt i daty (`od`, `doDnia` jako RRRR-MM-DD) są te same co w chwili wejścia w edycję.
const tenSamSprzetIDaty = (pristine, ids, od, doDnia) => {
  if (!pristine) return false;
  const poKolei = (lista) => (Array.isArray(lista) ? [...lista].sort((a, b) => a - b) : []);
  return JSON.stringify(poKolei(ids)) === JSON.stringify(poKolei(pristine.equipment_ids))
    && od === formatYMD(pristine.start_date)
    && doDnia === formatYMD(pristine.end_date);
};

const ReservationFormView = ({ reservation, onClose, onDelete, onSave, onEditingChange, onHandoverChange }) => {
  const { t, currentLanguage } = useTranslation();
  // Kwoty na ekranie jednym zapisem z resztą panelu (utils/format.js#formatMoney: „240,00 zł”).
  // Dawniej `toFixed(2)` z kropką, choć pola kwot w tym samym oknie przyjmują przecinek (test na
  // iPadzie, 18.10.2026). Tylko do wyświetlania: liczby do serwera i do pól idą bez zmian.
  const kwotaZl = (v) => formatMoney(v, { locale: currentLanguage });
  const toast = useToast();
  // F.2: odmowa planu (403 premium_required) jako komunikat z „Chcę Premium”.
  const pokazBladPremium = usePremiumBlad();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [availableEquipment, setAvailableEquipment] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setSaving] = useState(false);
  // Rodzic (kalendarz) musi wiedzieć, czy trwa edycja (12.10.2026): po zdarzeniu z serwera podaje
  // oknu świeży wiersz rezerwacji, a nowy obiekt w trybie edycji wczytałby formularz od nowa
  // i skasował wpisane zmiany. Uchwyt przez ref, żeby nowa funkcja rodzica przy każdym renderze
  // nie zgłaszała stanu ponownie; przy zamknięciu okna zgłaszamy koniec edycji.
  const onEditingChangeRef = useRef(onEditingChange);
  onEditingChangeRef.current = onEditingChange;
  useEffect(() => {
    if (typeof onEditingChangeRef.current === 'function') onEditingChangeRef.current(isEditing);
  }, [isEditing]);
  useEffect(() => () => {
    if (typeof onEditingChangeRef.current === 'function') onEditingChangeRef.current(false);
  }, []);
  // P3.7: suma dopłat online (type=balance, netto po zwrotach) — koryguje „Pozostało
  // do zapłaty" i steruje przyciskami dopłaty. Fetch per-rezerwacja działa też dla
  // pracownika (endpoint wymaga admina tylko dla PEŁNEJ listy); best-effort przy sieci.
  const [balancePaidGrosze, setBalancePaidGrosze] = useState(0);
  // Zaliczki z rejestru płatności (12.10.2026): ile wpisów i ile netto po zwrotach. `null` = lista
  // płatności jeszcze nie wróciła (albo brak dostępu), wtedy liczy się flaga advance_paid jak dotąd.
  const [zaliczkiZRejestru, setZaliczkiZRejestru] = useState(null);
  const [offlineBusy, setOfflineBusy] = useState(false); // P3.10: blokada podwójnego zapisu offline
  const offlineBusyRef = useRef(false);
  const [customAmountZl, setCustomAmountZl] = useState(''); // P3.14: link na dowolną kwotę
  // P3.8: aktywna kaucja online (pending/authorized/captured) — steruje przyciskami kaucji.
  const [depositPayment, setDepositPayment] = useState(null);
  // X.1: bieżący stan wydania/zwrotu z panelu (null, dopóki panel się nie wczytał). Status w oknie
  // liczy się z niego od razu, a listy w kalendarzu odświeżają się z opóźnieniem po serii skanów.
  const [handoverItems, setHandoverItems] = useState(null);
  const handoverRefreshRef = useRef(null);
  const onHandoverChangeRef = useRef(onHandoverChange);
  onHandoverChangeRef.current = onHandoverChange;
  // Numer rezerwacji idzie do rodzica wprost: ten sam sygnał przychodzi też PO zamknięciu okna,
  // a rodzic sam sprawdza, czy okno tej rezerwacji jest jeszcze otwarte (calendar/index.js).
  const reservationIdRef = useRef(reservation?.id);
  reservationIdRef.current = reservation?.id;
  useEffect(() => () => {
    // okno zamknięte w trakcie odliczania: listy i tak trzeba odświeżyć
    if (handoverRefreshRef.current) {
      clearTimeout(handoverRefreshRef.current);
      handoverRefreshRef.current = null;
      if (onHandoverChangeRef.current) onHandoverChangeRef.current(reservationIdRef.current);
    }
  }, []);
  const handleHandoverItems = useCallback((items, { saved } = {}) => {
    setHandoverItems(items);
    if (!saved || !onHandoverChangeRef.current) return;
    // Skan za skanem: jedno odświeżenie list (5 zapytań) po serii, nie po każdej sztuce.
    if (handoverRefreshRef.current) clearTimeout(handoverRefreshRef.current);
    handoverRefreshRef.current = setTimeout(() => {
      handoverRefreshRef.current = null;
      if (onHandoverChangeRef.current) onHandoverChangeRef.current(reservationIdRef.current);
    }, 1200);
  }, []);
  // Oczekujace prosby o ZWYKLA platnosc (nie kaucje), ktore NIOSA slad wysylki — po nich
  // pokazujemy, kiedy poszedl link.
  //
  // Trzymamy je PER TYP, a nie jedna wybrana. Poprzednia wersja brala pierwsza z ta sama data dla
  // kazdej sekcji, wiec po oplaceniu zaliczki gotowka (record-offline NIE anuluje wiersza `pending`,
  // tylko ustawia advance_paid) sekcja doplaty pokazywala date wysylki linku do ZALICZKI, ktorego
  // doplata w ogole nie dotyczy. Komentarz obok twierdzil, ze filtr po obecnosci sladu to domyka —
  // nie domykal, bo taki wiersz slad wlasnie NIESIE. Znalezione w code review PR #125.
  //
  // Tablica jest z definicji krotka (oczekujace, nie-kaucje, ze sladem), wiec `.find()` po typie
  // przy renderze jest bez znaczenia; ciezki filtr po pelnej liscie platnosci zostaje w `fetch`.
  const [prosbyZeSladem, setProsbyZeSladem] = useState([]);
  // Stan przyciskow wysylki i kopiowania linku. Wczesniej szedl WPROST do DOM (btn.disabled,
  // btn.textContent) i dzialalo to dopoki React nie przerysowal przycisku. Po P3.30 przerysowuje:
  // `wczytajPlatnosci` zmienia kwote w etykiecie, React nadpisuje napis „Wyslano do klienta", a
  // `disabled` ustawione imperatywnie NIE jest propsem, wiec React go nie cofa. Przycisk wygladal
  // jak gotowy do uzycia i byl martwy az do przeladowania. Znalezione w code review PR #125.
  //
  // Warunki trzech sekcji platnosci sa ROZLACZNE (zaliczka / doplata / calosc), ale widoczna sekcja
  // zmienia sie w czasie zycia okna: od PR #212 kalendarz po zdarzeniu z serwera podaje swiezy
  // wiersz, wiec po oplaceniu zaliczki okno przechodzi z sekcji zaliczki do sekcji doplaty w tym
  // samym montazu. Jeden stan bez wskazania sekcji zostawial wtedy przycisk linku do reszty
  // wylaczony z napisem „Wyslano do klienta”, choc linku do reszty nikt nie wyslal (przeglad PR
  // #212). Dlatego stan pamieta, ktorej sekcji dotyczy (`sekcjaPlatnosci`), w innej sekcji jest
  // pusty, a po zmianie sekcji jest zapominany (efekt przy `sekcjaPlatnosci`).
  const [wysylka, setWysylka] = useState(BEZ_STANU_PRZYCISKU); // stan: null | 'wysylanie' | 'wyslane'
  const [kopiowanie, setKopiowanie] = useState(BEZ_STANU_PRZYCISKU); // stan: null | 'trwa' | 'skopiowano' | 'recznie'
  // Ktora rezerwacja siedzi w powyzszych stanach — patrz `wczytajPlatnosci`.
  const wczytanaRezerwacjaRef = useRef(null);
  const [depositBusy, setDepositBusy] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);

  const [detailEquipment, setDetailEquipment] = useState([]);
  const [detailEquipmentFailed, setDetailEquipmentFailed] = useState(0); // U.9: ile sztuk /api/equipment/:id nie dało się wczytać (cichy fallback → komunikat + retry)
  const formContainerRef = useRef(null);
  const [conflicts, setConflicts] = useState([]);
  const [saveError, setSaveError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  // U.26: które pola kwot pokazują limit. Sam komunikat jest wyliczany przy renderze (niżej, przy
  // `pricing`), więc nie może zostać ze starą kwotą. `fieldErrors.discount`/`.advanceAmount` to już
  // wyłącznie znacznik błędu zgłoszonego przez SERWER.
  const {
    shown: amountChecksShown, show: showAmountCheck, hide: hideAmountCheck,
    showAll: showAmountChecks, hideAll: hideAmountChecks,
  } = useAmountChecks();
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  const [priceQuote, setPriceQuote] = useState(null); // P7.2: { key, data } from /api/price-quote
  const [quotePending, setQuotePending] = useState(false); // P7.5: debounced fetch in flight
  const tabelaDniPlatnych = useTabelaDniPlatnych();
  // Klucz zaznaczenia (sprzęt + daty), dla którego wycena już WRÓCIŁA, udana albo nie. `quotePending`
  // do tego nie wystarcza: ustawia go efekt, więc w renderze, w którym zmienia się sprzęt, ma jeszcze
  // starą wartość i efekt dopasowania kwot brał cenę lokalną (bez reguł cennika) za ostateczną.
  const [quoteSettledKey, setQuoteSettledKey] = useState(null);
  // Kwota zapisana przy rezerwacji; 0 to brak kwoty (blokada z kalendarza sprzętu, stare wiersze).
  const zapisanaKwota = Number(reservation?.subtotal ?? reservation?.total_price ?? 0) || 0;

  // U.9: wydzielone z efektu, żeby dropdown per-sztuka mógł ponowić pobranie po błędzie
  // (część /api/equipment/:id zapadała cicho → dokumentację dało się wpiąć tylko do „Cały zestaw").
  // Pobranie zależy od LISTY ID, nie od tożsamości obiektu rezerwacji: rodzic po każdym
  // odświeżeniu listy (`refreshReservations`) podaje świeży obiekt z tymi samymi danymi.
  // Przy zależności od obiektu każde takie odświeżenie ponawiało te same zapytania i —
  // bo licznik błędów zeruje się przed fetchami — mrugało komunikatem o nieudanym wczytaniu.
  const equipmentIdsKey = useMemo(() => {
    const ids = Array.isArray(reservation?.equipment)
      ? reservation.equipment
      : Array.isArray(reservation?.equipment_ids)
        ? reservation.equipment_ids
        : [];
    return ids.join(',');
  }, [reservation]);

  const loadDetailEquipment = useCallback(async () => {
    const ids = equipmentIdsKey ? equipmentIdsKey.split(',') : [];

    if (!ids.length) {
      setDetailEquipment([]);
      setDetailEquipmentFailed(0);
      return;
    }

    // U.9: wyzeruj licznik PRZED awaitem, żeby przy zmianie rezerwacji / retry nie
    // wisiał stary komunikat z poprzednim wynikiem, zanim nowe fetche się rozstrzygną.
    setDetailEquipmentFailed(0);

    const items = await Promise.all(
      ids.map(id =>
        fetch(`${API_BASE}/api/equipment/${id}`, { credentials: 'include' })
          .then(r => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    );

    const loaded = items.filter(Boolean);
    setDetailEquipment(loaded);
    setDetailEquipmentFailed(ids.length - loaded.length); // U.9: >0 → część sprzętu nie wczytana
  }, [equipmentIdsKey]);

  useEffect(() => { loadDetailEquipment(); }, [loadDetailEquipment]);

  // Wydzielone z efektu, zeby dalo sie wywolac PO wysylce linku. Bez tego slad pojawial sie
  // dopiero przy ponownym otwarciu panelu, wiec kolega przy ladzie go nie widzial (P3.30).
  // JEDNA sekcja prośby o płatność zamiast trzech kopii (P3.28).
  //
  // Stany, w których się renderuje, wykluczają się wzajemnie: zaliczka nieopłacona, zaliczka
  // opłacona z dopłatą, rezerwacja bez zaliczki. Kod był w trzech egzemplarzach po ~100 linii,
  // różniących się PIĘCIOMA wartościami — reszta, łącznie z obsługą błędów i stylami, bajt w bajt.
  //
  // Duplikacja zdążyła kosztować funkcjonalnie, co wyszło dopiero w audycie: „Zapłać kartą na
  // miejscu" istniało w dwóch kopiach z trzech, a „Link na inną kwotę (rata)" tylko w jednej.
  // Klient, który opłacił zaliczkę i chciał dopłacić resztę kartą przy ladzie, nie miał czym.
  // Funkcja lądowała w tej kopii, na którą akurat patrzył autor.
  //
  // ZWYKŁA FUNKCJA, nie komponent: gdyby to był komponent zdefiniowany w ciele
  // ReservationFormView, React widziałby przy każdym renderze nowy typ i przemontowywał poddrzewo,
  // co gubiłoby fokus w polu kwoty raty. Wywołanie funkcji wstawia elementy w miejscu.
  const renderSekcjaPlatnosci = ({ mode, etykietaWyslij, etykietaKopiuj, tytulKarty, typOffline, pokazRate, uwaga = null }) => {
    // F.6: termin wpłaty tylko przy linku do zaliczki (sekcja bez trybu) i przed wydaniem sprzętu.
    const zTerminem = !mode && !sprzetWydany && !!terminWplaty.terminIso;
    const cialoLinku = (send) => JSON.stringify({
      reservation_id: reservation.id, send, ...(mode ? { mode } : {}),
      ...(zTerminem ? { payment_deadline_at: terminWplaty.terminIso } : {}),
    });
    // Zaznaczony termin poza zakresem: nie wysyłamy linku bez terminu, który pracownik chciał ustawić.
    const terminDoPoprawki = () => {
      if (mode || !terminWplaty.wstrzymaj) return false;
      toast.error(t('paymentDeadline.invalid'));
      return true;
    };
    // Po wysłaniu linku z terminem: termin od razu w szczegółach (zdarzenie z serwera odświeży wiersz).
    const poLinku = (data) => {
      if (!data) return;
      poZdjeciuTerminu(data);
      if (mode) return;
      if (data.payment_deadline_at) setTerminLokalny({ rezerwacja: reservation.id, wartosc: data.payment_deadline_at });
      if (zTerminem) { terminWplaty.setWlaczony(false); terminWplaty.setPole(''); }
    };
    // Stan przyciskow tylko tej sekcji (patrz `wysylka`); tryb wyznacza sekcje jednoznacznie.
    const sekcja = mode || 'advance';
    const statusWysylki = wysylka.sekcja === sekcja ? wysylka.stan : null;
    const statusKopiowania = kopiowanie.sekcja === sekcja ? kopiowanie.stan : null;
    const setStatusWysylki = (stan) => setWysylka({ sekcja, stan });
    const setStatusKopiowania = (stan) => setKopiowanie({ sekcja, stan });
    return (
      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 320 }}>
        {uwaga && (
          <div data-cy="payment-price-list-note" style={{ fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>{uwaga}</div>
        )}
        <SladWysylki platnosc={prosbyZeSladem.find((x) => x.type === typOffline) || null} />
        {!linkiOnline && (
          <PremiumNotice feature="payment_links" compact data-cy="payment-links-premium">
            {t('reservationForm.payment.linksPremium')}
          </PremiumNotice>
        )}
        <fieldset disabled={!linkiOnline} style={stylBlokadyLinkow} data-cy="payment-links-lock">
        {!mode && !sprzetWydany && (
          <PaymentDeadlineField
            termin={terminWplaty}
            premium={linkiOnline}
            bezEmaila={!String(reservation.email || '').trim()}
            id={`payment-deadline-${reservation.id}`}
          />
        )}
        {/* P3.3-B: klient płaci U SIEBIE — pracownik wysyła link mailem lub kopiuje. */}
        {reservation.email && (
          <button
            type="button"
            disabled={statusWysylki !== null}
            style={{ width: '100%', padding: '10px 14px', border: 'none', borderRadius: 8, background: 'var(--primary)', color: 'var(--primary-contrast)', fontWeight: 600, cursor: statusWysylki ? 'default' : 'pointer', fontSize: '0.85rem', opacity: statusWysylki ? 0.85 : 1 }}
            onClick={async () => {
              if (statusWysylki !== null) return;
              if (terminDoPoprawki()) return;
              setStatusWysylki('wysylanie');
              try {
                const csrf = localStorage.getItem('csrfToken') || '';
                const r = await fetch(`${API_BASE}/api/payments/send-link`, {
                  method: 'POST', credentials: 'include',
                  headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
                  body: cialoLinku(true),
                });
                const data = await r.json().catch(() => ({}));
                if (!r.ok && pokazBladPremium(data)) { setStatusWysylki(null); return; }
                if (!r.ok) throw new Error(data.error || t('reservationForm.payment.sendLinkError'));
                setStatusWysylki('wyslane');
                poLinku(data);
                wczytajPlatnosci();
              } catch (err) {
                setStatusWysylki(null);
                toast.error(err.message || t('reservationForm.payment.sendLinkError'));
              }
            }}
          >
            {statusWysylki === 'wysylanie' ? 'Wysyłanie…' : (statusWysylki === 'wyslane' ? 'Wysłano do klienta' : etykietaWyslij)}
          </button>
        )}
        <button
          type="button"
          disabled={statusKopiowania === 'trwa'}
          style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--primary)', borderRadius: 8, background: 'transparent', color: 'var(--primary)', fontWeight: 600, cursor: statusKopiowania === 'trwa' ? 'default' : 'pointer', fontSize: '0.85rem' }}
          onClick={async () => {
            if (statusKopiowania === 'trwa') return;
            if (terminDoPoprawki()) return;
            setStatusKopiowania('trwa');
            try {
              const csrf = localStorage.getItem('csrfToken') || '';
              const r = await fetch(`${API_BASE}/api/payments/send-link`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
                body: cialoLinku(false),
              });
              const data = await r.json().catch(() => ({}));
              if (!r.ok && pokazBladPremium(data)) { setStatusKopiowania(null); return; }
              if (!r.ok || !data.url) throw new Error(data.error || t('reservationForm.payment.prepareLinkError'));
              // F.6: przy terminie wpłaty tekst do wklejenia klientowi (SMS) niesie link i zdanie o terminie.
              const skopiowano = await copyTextToClipboard(data.copy_text || data.url);
              if (skopiowano) await potwierdzSkopiowanie(data.payment_id);
              poLinku(data);
              setStatusKopiowania(skopiowano ? 'skopiowano' : 'recznie');
              wczytajPlatnosci();
              setTimeout(() => setStatusKopiowania(null), 2000);
            } catch (err) {
              setStatusKopiowania(null);
              toast.error(err.message || t('common.error'));
            }
          }}
        >
          {statusKopiowania === 'skopiowano' ? 'Skopiowano' : (statusKopiowania === 'recznie' ? 'Skopiuj ręcznie' : etykietaKopiuj)}
        </button>
        {/* Płatność kartą przy ladzie. Brakowało jej w stanie „zaliczka opłacona, została dopłata",
            czyli dokładnie tam, gdzie klient stoi przy kasie z resztą do zapłaty (P3.28). */}
        {reservation.email && (
          <button
            type="button"
            title={tytulKarty}
            style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--input-border)', borderRadius: 8, background: 'transparent', color: 'var(--text)', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}
            onClick={async (e) => {
              const btn = e.currentTarget;
              if (btn.disabled) return;
              btn.disabled = true;
              const label = btn.textContent;
              btn.textContent = 'Przekierowanie…';
              try {
                const csrf = localStorage.getItem('csrfToken') || '';
                const r = await fetch(`${API_BASE}/api/payments/register`, {
                  method: 'POST', credentials: 'include',
                  headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
                  body: JSON.stringify({ reservation_id: reservation.id, email: reservation.email, ...(mode ? { mode } : {}) }),
                });
                const data = await r.json().catch(() => ({}));
                if (!r.ok && pokazBladPremium(data)) { btn.disabled = false; btn.textContent = label; return; }
                if (!r.ok || !data.redirectUrl) throw new Error(data.error || t('reservationForm.payment.initError'));
                window.location.href = data.redirectUrl;
              } catch (err) {
                btn.disabled = false;
                btn.textContent = label;
                toast.error(err.message || t('reservationForm.payment.initError'));
              }
            }}
          >
            Zapłać kartą na miejscu
          </button>
        )}
        {/* P3.14: link na DOWOLNĄ (częściową) kwotę — rata. Brakowało jej przy rezerwacji bez
            zaliczki, czyli w stanie, w którym rozłożenie płatności ma największy sens (P3.28).
            CELOWO nieobecna, dopóki zaliczka jest nieopłacona: rata tworzy wiersz typu „dopłata",
            a dopóki zaliczka wisi, prośbą do wysłania jest ona sama. */}
        {pokazRate && (
          <div style={{ borderTop: '1px dashed var(--input-border)', paddingTop: 8, marginTop: 2 }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 6 }}>Link na inną kwotę (np. rata):</div>
            {/* flexWrap: bez tego pole i dwa przyciski wypychaly sie poza kolumne
                i nachodzily na sekcje kaucji obok. */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="number" min="0" step="0.01" placeholder="zł"
                value={customAmountZl}
                onChange={(e) => setCustomAmountZl(e.target.value)}
                style={{ flex: '1 1 70px', minWidth: 0, padding: '7px 8px', border: '1px solid var(--input-border)', borderRadius: 8, background: 'var(--input-bg)', color: 'var(--text)', fontSize: '0.8rem' }}
              />
              <button type="button" style={offlineBtnStyle} onClick={() => sendCustomLink(true)}>Wyślij</button>
              <button type="button" style={offlineBtnStyle} onClick={() => sendCustomLink(false)}>Kopiuj</button>
            </div>
          </div>
        )}
        </fieldset>
        {/* P3.10: klient zapłacił FIZYCZNIE (gotówka, przelew, F.10: karta z terminala wypożyczalni,
            bez Stripe) — zapisz jako wpis płatności. Poza blokadą planu: wpłata na miejscu jest w Basic. */}
        <div style={{ borderTop: '1px dashed var(--input-border)', paddingTop: 8, marginTop: 2 }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 6 }}>Zapłacono na miejscu:</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['cash', 'transfer', 'card'].map((metoda) => (
              <button
                key={metoda}
                type="button"
                style={offlineBtnStyle}
                onClick={() => recordOfflinePayment(typOffline, metoda)}
                disabled={offlineBusy}
                data-cy={`offline-payment-${metoda}`}
              >
                {nazwaMetody(metoda, t)}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const wczytajPlatnosci = useCallback(() => {
    // Czyscimy TYLKO przy zmianie rezerwacji. Do P3.30 ta funkcja biegla wylacznie wtedy, wiec
    // bezwarunkowe zerowanie bylo niewidoczne; odkad wola sie ja po KAZDEJ wysylce, zerowanie
    // migalo w oczy: znikala linia „Zaplacono", kwoty w etykietach skakaly i po chwili wracaly.
    if (wczytanaRezerwacjaRef.current !== (reservation?.id ?? null)) {
      wczytanaRezerwacjaRef.current = reservation?.id ?? null;
      setBalancePaidGrosze(0);
      setZaliczkiZRejestru(null);
      setDepositPayment(null);
      setProsbyZeSladem([]);
      setWysylka(BEZ_STANU_PRZYCISKU);
      setKopiowanie(BEZ_STANU_PRZYCISKU);
    }
    if (!reservation?.id) return;
    fetch(`${API_BASE}/api/payments?reservation_id=${encodeURIComponent(reservation.id)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || !Array.isArray(data.payments)) return;
        // Prośba, po której pokazujemy datę wysyłki. NIE po prostu najnowsza oczekująca:
        // (1) po opłaceniu zaliczki gotówką stary wiersz `pending` zostaje (record-offline go nie
        //     anuluje, tylko ustawia advance_paid), więc sekcja dopłaty pokazywałaby datę wysyłki
        //     linku do ZALICZKI, którego dopłata nie dotyczy;
        // (2) otwarcie linku przez klienta zakłada NOWSZY wiersz techniczny bez śladu, który
        //     przesłaniał prawdziwy i data znikała.
        // Bierzemy więc oczekujące wiersze, które faktycznie NIOSĄ ślad, i dobieramy je do sekcji
        // po TYPIE (advance/balance) już przy renderze. Znalezione w code review PR #121 i #125.
        const oczekujace = data.payments.filter((x) => x.type !== 'deposit' && x.status === 'pending');
        setProsbyZeSladem(oczekujace.filter((x) => x.link_last_at));
        const sum = data.payments
          .filter((p) => p.type === 'balance' && ['paid', 'partially_refunded', 'refunded'].includes(p.status))
          .reduce((s, p) => s + (Number(p.amount_grosze) || 0) - (Number(p.amount_refunded_grosze) || 0), 0);
        setBalancePaidGrosze(Math.max(0, sum));
        // Zaliczki tą samą regułą co należność na serwerze (getReservationOutstanding, 12.10.2026):
        // suma netto KAŻDEGO rozliczonego wpisu zaliczki, więc częściowy zwrot zaliczki zwiększa
        // „Pozostało do zapłaty”, a druga opłacona zaliczka je zmniejsza.
        const zaliczki = data.payments
          .filter((p) => p.type === 'advance' && ['paid', 'partially_refunded', 'refunded'].includes(p.status));
        setZaliczkiZRejestru({
          liczba: zaliczki.length,
          grosze: zaliczki.reduce((s, p) => s + (Number(p.amount_grosze) || 0) - (Number(p.amount_refunded_grosze) || 0), 0),
        });
        // P3.8/P3.9: „żywa" kaucja — także po (częściowym) zwrocie.
        //
        // Pierwszeństwo ma kaucja FAKTYCZNIE ZABLOKOWANA, nie po prostu najnowsza. Lista przychodzi
        // posortowana malejąco po dacie, więc samo `find` brało świeższą prośbę `pending` i chowało
        // za nią blokadę, którą reconcile dopiero co odzyskał — a przyciski „Ściągnij"/„Zwolnij"
        // renderują się wyłącznie dla `authorized`, więc pieniądze klienta zostawały zablokowane
        // bez żadnej drogi wyjścia z panelu. Znalezione w code review PR #117.
        const kaucje = data.payments.filter((p) => p.type === 'deposit');
        const dep = kaucje.find((p) => ['authorized', 'captured', 'partially_refunded'].includes(p.status))
          || kaucje.find((p) => p.status === 'pending');
        setDepositPayment(dep || null);
      })
      .catch(() => { /* brak uprawnień/sieci — widok działa jak dotąd */ });
  }, [reservation]);

  useEffect(() => { wczytajPlatnosci(); }, [wczytajPlatnosci]);

  // P5.2: foto-dokumentacja stanu sprzętu (wydanie/zwrot) — zdjęcia i notatki uszkodzeń.
  const [photos, setPhotos] = useState([]);
  const [photoPhase, setPhotoPhase] = useState('pickup');
  const [photoNote, setPhotoNote] = useState('');
  const [photoEquipmentId, setPhotoEquipmentId] = useState('');
  const [photoFiles, setPhotoFiles] = useState([]); // P5.6: wybór wielu plików naraz
  const [photoBusy, setPhotoBusy] = useState(false);
  const photoFileInputRef = useRef(null);
  const photoAddFormRef = useRef(null); // U.9: cel scrolla dla „Dodaj zdjęcia zwrotu"
  // Komunikat porażki wysyłki zostaje do zamknięcia; kolejna wysyłka go zdejmuje, żeby po udanym
  // ponowieniu nie wisiała stara informacja, że wpis się nie zapisał.
  const photoErrorToastRef = useRef(null);
  const isAdminUser = localStorage.getItem('isAdmin') === '1' || localStorage.getItem('isSuperAdmin') === '1';
  // F.5: nowe linki do zapłaty (zaliczka, dopłata, kaucja na karcie) i płatność kartą przez Stripe
  // są w Premium; serwer odpowiada 403. Przyciski zostają widoczne jako zablokowane, a wpis wpłaty
  // na miejscu, zwroty i kaucje działają bez zmian. `fieldset disabled` wyłącza wszystko w środku.
  const linkiOnline = maPremium();
  // F.6: termin wpłaty zaliczki. Pole przy linku do zaliczki, a w szczegółach termin, dopóki serwer
  // go pilnuje (`payment_deadline_active` z listy rezerwacji). Odpowiedź send-link i usunięcie
  // terminu zmieniają widok od razu; świeży wiersz z serwera (zdarzenie SSE) zastępuje stan lokalny.
  const terminWplaty = useTerminWplaty(reservation?.start_date);
  const [terminLokalny, setTerminLokalny] = useState(null);
  useEffect(() => { setTerminLokalny(null); }, [reservation?.id, reservation?.payment_deadline_at, reservation?.payment_deadline_active]);
  // Scalenie z X.1: z rezerwacji wydano sprzęt (stan z panelu wydania, a do jego wczytania z wiersza
  // listy). Serwer wtedy terminu nie pilnuje i nowego nie przyjmie (TERMIN_AKTYWNY_SQL, 400
  // `payment_deadline_handed_over`), więc nie ma pola terminu, a szczegóły terminu nie pokazują
  // od razu po skanie, zanim lista kalendarza się odświeży.
  const sprzetWydany = Array.isArray(handoverItems)
    ? handoverItems.some((i) => i.picked_up_at)
    : (reservation?.picked_up_equipment || []).length > 0;
  const terminWidoczny = sprzetWydany ? null : (terminLokalny && terminLokalny.rezerwacja === reservation?.id
    ? terminLokalny.wartosc
    : (reservation?.payment_deadline_active ? reservation.payment_deadline_at : null));
  // Nowy link zdejmuje termin, który minął albo zaraz minie (serwer, przegląd pakietu 3 FAZY F):
  // pracownik dowiaduje się o tym od razu, a szczegóły przestają pokazywać termin. Rezerwacja
  // z widgetu wraca wtedy do jego reguły (`widget_cancel_hours`), więc komunikat mówi, kiedy
  // zostanie anulowana, zamiast obiecywać, że nie zostanie (przegląd pakietu 3b).
  const poZdjeciuTerminu = (data) => {
    if (!data || !data.payment_deadline_removed) return;
    setTerminLokalny({ rezerwacja: reservation.id, wartosc: null });
    const godzinyWidgetu = godzinyPoPrzypomnieniuWidgetu(data.widget_cancel_hours);
    toast.info(godzinyWidgetu
      ? t('paymentDeadline.removedByLinkWidget', { hours: godzinyWidgetu })
      : t('paymentDeadline.removedByLink'));
  };
  const stylBlokadyLinkow = { border: 0, padding: 0, margin: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8, opacity: linkiOnline ? 1 : 0.55 };

  // U.9: szybkie wejście w dodawanie zdjęć zwrotu — ustawia fazę i przewija do formularza.
  const startReturnDocumentation = () => {
    setPhotoPhase('return');
    if (photoAddFormRef.current && photoAddFormRef.current.scrollIntoView) {
      photoAddFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const loadPhotos = useCallback(async () => {
    if (!reservation?.id) return;
    try {
      const r = await fetch(`${API_BASE}/api/reservation-photos/${reservation.id}`, { credentials: 'include' });
      const data = await r.json().catch(() => ({}));
      if (r.ok && Array.isArray(data.photos)) setPhotos(data.photos);
    } catch (_) { /* dokumentacja jest dodatkiem — widok działa bez niej */ }
  }, [reservation?.id]);

  useEffect(() => { setPhotos([]); loadPhotos(); }, [loadPhotos]);

  const addPhotoEntry = async () => {
    if (!photoFiles.length && !photoNote.trim()) { toast.error(t('reservationForm.photos.addRequired')); return; }
    if (photoErrorToastRef.current) { toast.dismiss(photoErrorToastRef.current); photoErrorToastRef.current = null; }
    setPhotoBusy(true);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const note = photoNote.trim();
      // P5.6: każdy plik = osobny wpis; notatka dopina się do PIERWSZEGO (albo sama, gdy brak zdjęć).
      const jobs = photoFiles.length ? photoFiles.map((f, i) => ({ file: f, note: i === 0 ? note : '' })) : [{ file: null, note }];
      const nieudane = await wyslijPoKolei(jobs, async (job) => {
        const fd = new FormData();
        fd.append('phase', photoPhase);
        if (job.note) fd.append('note', job.note);
        if (photoEquipmentId) fd.append('equipment_id', photoEquipmentId);
        if (job.file) {
          // Pomniejszone przed wysyłką (dysk produkcji), opis w utils/imageThumb.js.
          const zdjecie = await prepareUploadPhoto(job.file);
          fd.append('file', zdjecie);
          const thumb = await makeThumbnail(zdjecie); // P5.5
          if (thumb) fd.append('thumb', thumb, 'thumb.jpg');
        }
        const r = await fetch(`${API_BASE}/api/reservation-photos/${reservation.id}`, {
          method: 'POST', credentials: 'include', headers: { 'x-csrf-token': csrf }, body: fd,
        });
        if (!r.ok) {
          // Ten sam kształt co błąd axios w nowej rezerwacji, więc jedna funkcja czyta status
          // i przyczynę (pole error albo message) dla obu formularzy (utils/wysylkaDokumentacji.js).
          const data = await r.json().catch(() => ({}));
          throw Object.assign(new Error(`HTTP ${r.status}`), { response: { status: r.status, data } });
        }
      });
      if (nieudane.length) {
        // Zostaje do zamknięcia (duration 0): niesie przyczynę, a znikając po 7 s łatwo go przegapić,
        // patrząc na formularz wyżej (symulator iOS, 27.09.2026).
        photoErrorToastRef.current = toast.error(komunikatSzczegolow(nieudane, jobs.length, t), { duration: 0 });
      }
      // Notatka nieudanego wpisu zostaje, a zdjęcie wtedy, gdy warto je wysłać ponownie (brak
      // odpowiedzi, wygasła sesja, chwilowy błąd serwera); zdjęcie odrzucone przez serwer znika
      // (utils/wysylkaDokumentacji.js). Lista dokumentacji odświeża się zawsze: gdy odpowiedź nie
      // dotarła, wpis mógł się zapisać i komunikat prosi o sprawdzenie listy przed ponowieniem.
      const zostaje = pozostaleWFormularzu(nieudane);
      setPhotoNote(zostaje.zostawNotatke ? note : '');
      setPhotoFiles(zostaje.pliki);
      if (photoFileInputRef.current) photoFileInputRef.current.value = '';
      loadPhotos();
    } catch (e) {
      toast.error(e.message || t('common.error'));
    } finally {
      setPhotoBusy(false);
    }
  };

  const deletePhotoEntry = async (id) => {
    const ok = await confirm({ title: t('reservationForm.photos.deleteConfirmTitle'), tone: 'danger', confirmLabel: t('common.delete') });
    if (!ok) return;
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const r = await fetch(`${API_BASE}/api/reservation-photos/${reservation.id}/${id}`, {
        method: 'DELETE', credentials: 'include', headers: { 'x-csrf-token': csrf },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || t('common.error'));
      loadPhotos();
    } catch (e) {
      toast.error(e.message || t('common.error'));
    }
  };

  // P3.8: akcje kaucji (link do blokady / ściągnij / zwolnij) — odświeżają depositPayment.
  const depositAction = async (path, body, confirmMsg) => {
    if (confirmMsg) {
      const ok = await confirm({ title: confirmMsg, tone: 'primary' });
      if (!ok) return null;
    }
    setDepositBusy(true);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const r = await fetch(`${API_BASE}${path}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
        body: JSON.stringify(body || {}),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok && pokazBladPremium(data)) return null;
      if (!r.ok) throw new Error(data.error || t('reservationForm.deposit.operationError'));
      return data;
    } catch (e) {
      toast.error(e.message || t('common.error'));
      return null;
    } finally {
      setDepositBusy(false);
    }
  };

  const [editForm, setEditForm] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    city: '',
    postal_code: '',
    street: '',
    building_number: '',
    apartment_number: '',
    address: '',
    start_date: new Date(),
    end_date: new Date(),
    equipment_ids: [],
    additional_note: '',
    // Rabat i zaliczka to SUROWY TEKST pola (U.26, jak w Nowej rezerwacji): liczbę daje dopiero
    // utils/amountField.js w wyliczeniach i w payloadzie zapisu.
    discount_value: '',
    discount_unit: '%',
    advance_amount: '',
    advance_paid: false,     // NOWE
    deposit_override_raw: ''     // DODANE: pole do edycji kaucji (puste = użyj wyliczonej)
  });

  const buildEditFormFromReservation = useCallback((res) => {
    if (!res) return null;
    const toDate = (val) => (val ? parseYMD(val) : null);
    const normId = (v) => {
      if (v == null) return v;
      if (typeof v === 'number') return v;
      if (typeof v === 'string' && /^\d+$/.test(v)) return Number(v);
      return v;
    };
    const eqIds = Array.isArray(res.equipment)
      ? res.equipment.map(normId)
      : Array.isArray(res.equipment_ids)
        ? res.equipment_ids.map(normId)
        : [];
    return {
      firstname: res.firstname || '',
      lastname: res.lastname || '',
      email: res.email || '',
      phone: res.phone || '',
      city: res.city || '',
      postal_code: res.postal_code || '',
      street: res.street || '',
      building_number: res.building_number || '',
      apartment_number: res.apartment_number || '',
      address: res.address || '',
      start_date: toDate(res.start_date) || new Date(),
      end_date: toDate(res.end_date) || new Date(),
      equipment_ids: eqIds.filter(id => id != null),
      additional_note: res.additional_note || '',
      // Number() przed zamianą na tekst: serwer oddaje kwoty z kropką dziesiętną („150.50").
      discount_value: amountToRaw(Number(res.discount_value ?? res.discount ?? 0) || 0),
      discount_unit: normalizeDiscountUnit(res.discount_unit),
      advance_amount: amountToRaw(Number(res.advance_amount || 0) || 0),
      advance_paid: !!res.advance_paid,
      // Zapisana kaucja w zapisie pola (przecinek); 0 to też nadpisanie, więc nie amountToRaw.
      deposit_override_raw: res.deposit_override != null ? kwotaDoPola(res.deposit_override) : ''
    };
  }, []);

  // Migawka sprzętu/dat z chwili wejścia w edycję — pricing (niżej) porównuje się z nią,
  // żeby samo otwarcie edycji (bez żadnej zmiany) nie podmieniało zapisanej ceny rezerwacji
  // na świeżo przeliczoną (aktualna stawka dnia i reguły cenowe mogły się zmienić od rezerwacji).
  const pristineEditFormRef = useRef(null);

  useEffect(() => {
    if (!reservation || !isEditing) return;
    const mapped = buildEditFormFromReservation(reservation);
    if (mapped) {
      setEditForm(mapped);
      pristineEditFormRef.current = mapped;
      setConflicts([]);
      setSaveError('');
      // Formularz jest wczytany od nowa, więc znaczniki błędów i limity pod polami kwot (U.26)
      // dotyczyłyby wartości, których już w nim nie ma.
      setFieldErrors({});
      hideAmountChecks();
    }
  }, [reservation, isEditing, buildEditFormFromReservation, hideAmountChecks]);

  const normalizedEquipmentIds = useMemo(() => {
    if (!Array.isArray(editForm.equipment_ids)) return [];
    return editForm.equipment_ids
      .map(id => {
        if (id == null) return null;
        if (typeof id === 'number') return id;
        if (typeof id === 'string' && /^\d+$/.test(id)) return Number(id);
        return id;
      })
      .filter(id => id !== null && id !== undefined && id !== '');
  }, [editForm.equipment_ids]);

  const datesSignature = useMemo(
    () => `${formatYMD(editForm.start_date)}|${formatYMD(editForm.end_date)}`,
    [editForm.start_date, editForm.end_date]
  );

    const mergeEquipmentLists = useCallback((incomingList = [], prevList = []) => {
    const map = new Map();
    const normalizeId = (value) => {
      if (value == null) return null;
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value);
      return null;
    };
    const addList = (list = []) => {
      list.forEach(item => {
        if (!item) return;
        const id = normalizeId(item.id ?? item.equipment_id);
        if (id == null) return;
        const previous = map.get(id) || {};
        map.set(id, { ...previous, ...item, id });
      });
    };
    addList(incomingList);
    addList(prevList);
    addList(detailEquipment);
    normalizedEquipmentIds.forEach(id => {
      if (id != null && !map.has(id)) {
        map.set(id, { id, name: `Sprzęt #${id}` });
      }
    });
    return Array.from(map.values());
  }, [detailEquipment, normalizedEquipmentIds]);

  useEffect(() => {
    if (!isEditing || !reservation) return;

    const fetchSelectedEquipment = async () => {
      const ids = Array.isArray(reservation.equipment)
        ? reservation.equipment
        : Array.isArray(reservation.equipment_ids)
          ? reservation.equipment_ids
          : [];

      if (!ids.length) {
        setAvailableEquipment([]);
        return;
      }

      const items = await Promise.all(
        ids.map(id =>
          fetch(`${API_BASE}/api/equipment/${id}`, { credentials: 'include' })
            .then(r => (r.ok ? r.json() : null))
            .catch(() => null)
        )
      );

      setAvailableEquipment(items.filter(Boolean));
    };

    fetchSelectedEquipment();
  }, [isEditing, reservation]);
  

  // efekt wykonujący sprawdzenie konfliktów przy zmianie dat / listy sprzętu
  useEffect(() => {
    if (
      !isEditing ||
      !editForm.start_date ||
      !editForm.end_date ||
      normalizedEquipmentIds.length === 0
    ) {
      setConflicts([]);
      setSaveError('');
      setCheckingConflicts(false);
      return;
    }

    const controller = new AbortController();
    const payload = {
      start_date: formatYMD(editForm.start_date),
      end_date: formatYMD(editForm.end_date),
      equipment_ids: normalizedEquipmentIds,
      exclude_reservation_id: reservation?.id
    };

    setCheckingConflicts(true);
    const csrf = localStorage.getItem('csrfToken') || '';
    fetch(`${API_BASE}/api/reservation/check-conflicts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
      credentials: 'include',
      body: JSON.stringify(payload),
      signal: controller.signal
    })
      .then(res => res.json().catch(() => ({})).then(data => ({ res, data })))
      .then(({ res, data }) => {
        if (res.status === 409) {
          setConflicts(data.conflicts || []);
          setSaveError('');
          scrollFormToTop();
        } else if (res.ok) {
          setConflicts([]);
          setSaveError('');
        } else {
          setConflicts([]);
          setSaveError(data.message || 'Nie udało się zweryfikować dostępności sprzętu.');
          scrollFormToTop();
        }
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          setConflicts([]);
          setSaveError('Nie udało się zweryfikować dostępności sprzętu.');
          scrollFormToTop();
        }
      })
      .finally(() => setCheckingConflicts(false));

    return () => controller.abort();
  }, [isEditing, datesSignature, normalizedEquipmentIds, reservation?.id]);

  // Przycisk „Edytuj” stoi w przyklejonym pasku na dole okna, więc klika się go zwykle po
  // przewinięciu podglądu. Formularz edycji zaczyna się od góry: inaczej nagłówek i dane klienta
  // zostawały poza widokiem. Przed narysowaniem klatki, żeby nie było widać skoku.
  useLayoutEffect(() => {
    if (formContainerRef.current) formContainerRef.current.scrollTop = 0;
  }, [isEditing]);

  const scrollFormToTop = () => {
    if (!formContainerRef.current) return;
    formContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleInputChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleEquipmentToggle = (equipmentId) => {
    const normalize = (v) => {
      if (v == null) return v;
      if (typeof v === 'number') return v;
      if (typeof v === 'string' && /^\d+$/.test(v)) return Number(v);
      return v;
    };
    const id = normalize(equipmentId);
    setEditForm(prev => {
      const prevIds = Array.isArray(prev.equipment_ids) ? prev.equipment_ids.map(normalize) : [];
      const has = prevIds.includes(id);
      const nextIds = has ? prevIds.filter(i => i !== id) : [...prevIds, id];
      return { ...prev, equipment_ids: nextIds };
    });
  };

  const loadEditData = useCallback(async () => {
    if (!reservation) return;
    const params = new URLSearchParams({
      start: formatYMD(editForm.start_date),
      end: formatYMD(editForm.end_date)
    });
    const res = await fetch(`${API_BASE}/api/equipment/available?${params.toString()}`, { credentials: 'include' });
    if (!res.ok) throw new Error('Nie udało się pobrać sprzętu.');
    const data = await res.json();
    setAvailableEquipment(prev => mergeEquipmentLists(data, prev));
  }, [reservation, editForm.start_date, editForm.end_date, mergeEquipmentLists]);

  // P7.2: debounced server quote (respects org price rules); falls back to the local
  // calc below on error or while in flight. Server applies the same paid days,
  // so results match exactly when no rules are configured.
  // N.6: zapytanie o klasy (rezerwacja ze sztukami z klas) wycenia serwer sztukami klas po cenach klas,
  // tą samą funkcją co zapis; ceny lokalnej (stawki sprzętu z magazynu, bez niedobranych sztuk) tu nie ma.
  const zapytanieKlas = Number(reservation?.class_units) > 0;
  const idRezerwacji = reservation?.id;
  // Nieudana wycena zapytania o klasy dla zaznaczenia `key`: `brak` (klasa bez ceny) i komunikat serwera.
  const [problemWycenyKlas, setProblemWycenyKlas] = useState(null);
  useEffect(() => {
    if (!isEditing) return undefined;
    const ids = Array.isArray(editForm.equipment_ids) ? editForm.equipment_ids : [];
    const from = formatYMD(editForm.start_date);
    const to = formatYMD(editForm.end_date);
    if (!ids.length || !from || !to) { setPriceQuote(null); setQuotePending(false); return undefined; }
    const key = quoteKey(ids, from, to);
    // N.3: rezerwacja bez zapisanej kwoty bierze cenę z tej wyceny, ale jej edycja, która nie zmienia
    // sprzętu ani dat, to nie nowa wycena, więc tabela firmy jej nie dotyczy (bez tego sam zapis
    // notatki utrwalał kwotę z tabeli, inną niż w podglądzie).
    const regulaDomyslna = zapisanaKwota <= 0 && tenSamSprzetIDaty(pristineEditFormRef.current, ids, from, to);
    let cancelled = false;
    setQuotePending(true);
    const timer = setTimeout(async () => {
      if (zapytanieKlas) {
        const w = await fetchClassRequestQuote(idRezerwacji, ids, from, to);
        if (!cancelled) {
          setPriceQuote(w.data ? { key, data: w.data } : null);
          setProblemWycenyKlas(w.data ? null : { key, brak: !!w.brak, message: w.message || '' });
          setQuotePending(false);
          setQuoteSettledKey(key);
        }
        return;
      }
      const data = await fetchPriceQuote(ids, from, to, { regulaDomyslna });
      if (!cancelled) {
        setPriceQuote(data ? { key, data } : null);
        setQuotePending(false);
        // Także po nieudanej wycenie: wtedy ostateczna jest cena lokalna i nie ma na co czekać.
        setQuoteSettledKey(key);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [isEditing, editForm.equipment_ids, editForm.start_date, editForm.end_date, zapisanaKwota, zapytanieKlas, idRezerwacji]);

  // N.6: podgląd rezerwacji BEZ zapisanej kwoty bierze cenę z tej samej wyceny, z której serwer liczy
  // link do zapłaty, fakturę i Statystyki (POST /api/price-quote z regułą domyślną dni płatnych, jak
  // edycja bez zmiany sprzętu i dat): ceny dynamiczne, stawka od 8. dnia i zestawy. Dotąd liczył sam,
  // stawką razy dni płatne, bez reguł i zestawów. Rezerwacja ze znacznikiem „należność z cennika”
  // (migracja 052) zostaje przy liczeniu lokalnym, jak dotąd; po nieudanej wycenie też liczy panel.
  const podgladZWyceny = !isEditing && zapisanaKwota <= 0 && !reservation?.amount_needs_price_list;
  const podgladOd = reservation ? formatYMD(parseYMD(reservation.start_date)) : '';
  const podgladDo = reservation ? formatYMD(parseYMD(reservation.end_date)) : '';
  const podgladKlucz = podgladZWyceny && equipmentIdsKey && podgladOd && podgladDo
    ? quoteKey(equipmentIdsKey.split(',').map(Number), podgladOd, podgladDo)
    : null;
  const [podgladWycena, setPodgladWycena] = useState(null); // { key, data }, data = null po nieudanej wycenie
  useEffect(() => {
    if (!podgladKlucz) return undefined;
    let anulowane = false;
    const [ids, od, dd] = podgladKlucz.split('|');
    fetchPriceQuote(ids.split(',').map(Number), od, dd, { regulaDomyslna: true }).then((data) => {
      if (!anulowane) setPodgladWycena({ key: podgladKlucz, data });
    });
    return () => { anulowane = true; };
  }, [podgladKlucz]);
  const podgladWycenaTrwa = !!podgladKlucz && podgladWycena?.key !== podgladKlucz;
  const podgladKwota = podgladKlucz && podgladWycena?.key === podgladKlucz && podgladWycena.data
    ? Number(podgladWycena.data.grandTotal)
    : null;

  const pricing = useMemo(() => {
    try {
      const daysLocal = daysInclusive(editForm.start_date, editForm.end_date);
      const isLongTermRental = daysLocal > 7;
      const selectedEq = (availableEquipment || [])
        .filter(eq => Array.isArray(editForm.equipment_ids) && editForm.equipment_ids.includes(eq.id));

      // Sprzęt i daty NIE zmienione od wejścia w edycję → trzymamy się ZAPISANEJ kwoty
      // rezerwacji (jak w trybie podglądu), zamiast przeliczać na aktualnej stawce dnia —
      // ta mogła się zmienić od momentu rezerwacji (reguły cenowe, cennik sprzętu), więc samo
      // otwarcie edycji bez żadnej zmiany nie może podmieniać ceny, którą klient faktycznie ma zapłacić.
      const pristine = pristineEditFormRef.current;
      const unchangedSelection = tenSamSprzetIDaty(
        pristine, editForm.equipment_ids, formatYMD(editForm.start_date), formatYMD(editForm.end_date),
      );

      const currentQuoteKey = quoteKey(editForm.equipment_ids, formatYMD(editForm.start_date), formatYMD(editForm.end_date));
      const quoteData = (priceQuote && priceQuote.key === currentQuoteKey) ? priceQuote.data : null;
      const quotedTotal = quoteData ? quoteData.grandTotal : null;
      // N.3: dni płatne z wyceny serwera, a do jej nadejścia i po jej błędzie lokalny zapas. Tabela firmy
      // działa tylko przy nowej wycenie, więc zapas bez zmiany sprzętu i dat liczy regułą domyślną.
      const billableDays = dniPlatneZWyceny(quoteData, dniPlatne(daysLocal, unchangedSelection ? null : tabelaDniPlatnych));
      // Cena lokalna zaokrąglona do grosza: suma stawek bywa ułamkiem tuż poniżej kwoty z ekranu
      // (2 × 19,99 × 5 = 199,89999999999998), a ta liczba idzie do zapisu jako `total_price`
      // i serwer porównuje z nią rabat w zł.
      const subtotalLocal = (unchangedSelection && zapisanaKwota > 0)
        ? zapisanaKwota
        : quotedTotal != null ? quotedTotal : roundToCents(selectedEq.reduce(
          (s, eq) => s + getEquipmentDailyRate(eq, isLongTermRental),
          0
        ) * billableDays);
      // Czy ta cena jest już ostateczna: zapisana kwota rezerwacji (nic nie zmieniono) albo wycena
      // z serwera wróciła dla TEGO zaznaczenia. Liczone tu, z tych samych danych co cena, a nie
      // z flagi `quotePending`, która w renderze ze zmianą sprzętu jest jeszcze nieaktualna.
      // N.6: zapytanie o klasy bez wyceny z serwera nie ma ceny ostatecznej (cena lokalna pomija sztuki
      // klas), więc zapis czeka, a po nieudanej wycenie formularz mówi dlaczego (`classQuoteProblem`).
      const zapisanaBezZmian = unchangedSelection && zapisanaKwota > 0;
      const wycenaWrocila = quoteSettledKey === currentQuoteKey;
      const settled = zapisanaBezZmian || (wycenaWrocila && (!zapytanieKlas || quoteData != null));
      const classQuoteProblem = zapytanieKlas && !zapisanaBezZmian && wycenaWrocila && quoteData == null
        && problemWycenyKlas && problemWycenyKlas.key === currentQuoteKey
        ? problemWycenyKlas
        : null;
      // Rabat w zł wg jednostki, zawsze w granicach 0…cena sprzętu, w pełnych groszach jak faktura
      // (utils/amountField.js, N.6); różnica też w groszach, bez reszty zmiennoprzecinkowej.
      const discountAmt = discountInPln(editForm.discount_value, normalizeDiscountUnit(editForm.discount_unit), subtotalLocal);
      const totalLocal = Math.max(0, Math.round(subtotalLocal * 100) - Math.round(discountAmt * 100)) / 100;
      // P3.15 follow-up: opłacenie zaliczki wynika z płatności (reservation.advance_paid), nie z pola
      // formularza; przy opłaconej używamy ZAPISANEJ kwoty (backend i tak jej nie zmienia przy edycji).
      const effectiveToPay = Math.max(0, totalLocal - (reservation.advance_paid ? Number(reservation.advance_amount || 0) : 0));
      const depositLocal = selectedEq.reduce(
        (s, eq) => s + (parseFloat(eq.bail) || 0),
        0
      ); // zmiana: już NIE mnożymy przez billableDays
      return {
        days: daysLocal,
        billableDays,
        subtotal: subtotalLocal,
        discountAmount: discountAmt,
        total: totalLocal,
        effectiveToPay,      // NOWE
        deposit: depositLocal,
        settled,
        classQuoteProblem,
        // Cena sprzętu to kwota zapisana przy rezerwacji, a nie dzisiejsza wycena.
        savedAmount: unchangedSelection && zapisanaKwota > 0,
        // Formularz z chwili wejścia w edycję, względem którego policzono tę cenę (patrz efekt niżej).
        basis: pristine
      };
    } catch {
      return { days: 0, subtotal: 0, discountAmount: 0, total: 0, effectiveToPay: 0, deposit: 0, settled: false, basis: null };
    }
  }, [editForm, availableEquipment, priceQuote, quoteSettledKey, reservation, zapisanaKwota, tabelaDniPlatnych, zapytanieKlas, problemWycenyKlas]);

  // Zaliczka NIEOPŁACONA i rabat w zł to tylko plan — gdy usunięcie sprzętu albo krótszy termin
  // obniża cenę sprzętu poniżej wpisanej kwoty, przycinamy ją do nowej wartości automatycznie
  // (zamiast wymuszać ręczną poprawkę i blokować zapis błędem „Maksymalnie X").
  // U.26: efekt reaguje WYŁĄCZNIE na zmianę ceny sprzętu, nigdy na wpisywanie w polach kwot —
  // tam limit pokazuje komunikat po opuszczeniu pola i walidacja przy zapisie. Dawniej zależał
  // też od wpisanej zaliczki, więc przycinał ją przy każdym znaku. Trzy warunki, każdy po błędzie:
  //  1. Cena musi dotyczyć formularza, który jest teraz w stanie (`pricing.basis`). Po „Anuluj”
  //     i ponownym wejściu w edycję pierwszy render ma jeszcze formularz z porzuconej sesji, a efekt
  //     wyżej w tym samym commicie wczytuje go od nowa; cena z tego renderu nałożona na świeży
  //     formularz po cichu obniżała zapisaną zaliczkę i rabat.
  //  2. Cena musi być ostateczna (`pricing.settled`). Do powrotu wyceny liczy ją panel, bez reguł
  //     cennika, więc przy dopłacie (np. weekendowej) jest NIŻSZA od prawdziwej i kwota przycięta
  //     do niej zostawałaby za mała.
  //  3. Nieznanej ceny (0: sprzęt się nie wczytał, brak dat) nie używamy, bo przycięcie do zera
  //     skasowałoby obie kwoty.
  // Zaliczka OPŁACONA zostaje bez zmian — to realnie pobrane pieniądze, a backend i tak nie zmienia
  // jej przy edycji (patrz komentarz w `pricing` wyżej).
  const equipmentPrice = pricing?.subtotal || 0;
  const priceSettled = !!pricing?.settled;
  const pricingBasis = pricing?.basis ?? null;
  const advanceIsPaid = !!reservation?.advance_paid;
  // N.6: dlaczego ceny zapytania o klasy nie da się przeliczyć (komunikat serwera przy klasie bez ceny).
  const komunikatWycenyKlas = pricing?.classQuoteProblem
    ? ((pricing.classQuoteProblem.brak && pricing.classQuoteProblem.message) || t('reservationForm.classQuote.failed'))
    : '';
  // Cena, dla której kwoty były już dopasowane: dopasowujemy raz na każdą NOWĄ cenę, więc samo
  // zakończenie wyceny bez zmiany ceny nie przycina tego, co ktoś zdążył wpisać w międzyczasie
  // (pilnuje `calendar/__tests__/edycjaRabatu.test.js`).
  const cappedForPriceRef = useRef(null);
  useEffect(() => {
    if (!isEditing) { cappedForPriceRef.current = null; return; }
    if (pricingBasis !== pristineEditFormRef.current) return;
    if (!priceSettled || !(equipmentPrice > 0)) return;
    if (cappedForPriceRef.current === equipmentPrice) return;
    cappedForPriceRef.current = equipmentPrice;
    setEditForm(prev => {
      const capped = capAmountsToPrice(
        { discount: prev.discount_value, unit: prev.discount_unit, advance: prev.advance_amount },
        equipmentPrice,
        { advancePaid: advanceIsPaid },
      );
      if (capped.discount === prev.discount_value && capped.advance === prev.advance_amount) return prev;
      return { ...prev, discount_value: capped.discount, advance_amount: capped.advance };
    });
  }, [isEditing, pricingBasis, priceSettled, equipmentPrice, advanceIsPaid]);

  // Kody błędów pól kwot liczone z BIEŻĄCYCH wartości i limitów (komunikat nie jest stanem, patrz
  // utils/useAmountChecks.js). Rabat w zł porównujemy z ceną sprzętu, od której podsumowanie go
  // odejmuje (serwer odrzuca rabat większy niż wysłana kwota), zaliczkę z kwotą po rabacie.
  // Przy nieznanej cenie (0) nie ma z czym porównać: inaczej rezerwacji z zapisaną kwotą 0 nie dało
  // się zapisać („Maksymalnie 0,00 zł"), a wyczyszczone daty dawały ten komunikat zamiast błędu
  // o brakującej dacie. Zaliczka tylko NIEOPŁACONA: dla opłaconej backend i tak ignoruje
  // advance_amount przy edycji, więc blokowanie zapisu reszty zmian byłoby bez sensu.
  const amountI18n = { t, locale: currentLanguage };
  const discountUnit = normalizeDiscountUnit(editForm.discount_unit);
  const advanceLimit = Math.max(0, pricing?.total || 0);
  const discountCode = discountError(editForm.discount_value, discountUnit, equipmentPrice);
  const advanceCode = advanceIsPaid
    ? null
    : advanceError(editForm.advance_amount, advanceLimit, equipmentPrice > 0);
  const discountLimitNote = amountChecksShown.discount
    ? amountErrorMessage(discountCode, equipmentPrice, amountI18n)
    : null;
  const advanceLimitNote = amountChecksShown.advance
    ? amountErrorMessage(advanceCode, advanceLimit, amountI18n)
    : null;
  // Komunikat u góry okna po zapisie zatrzymanym na kwotach: widoczny tylko w edycji i tylko, dopóki
  // któraś kwota nadal jest błędna, więc znika sam, gdy ktoś ją poprawi albo dopasuje ją zmiana ceny.
  const amountsBanner = isEditing && amountChecksShown.submitted && (discountCode || advanceCode)
    ? t('amountField.error.checkAmounts')
    : '';

  // Pisanie w polu chowa jego limit (wróci po opuszczeniu pola) i zdejmuje znacznik błędu z serwera.
  const setAmountField = (field, check, serverKey, raw) => {
    setEditForm(prev => ({ ...prev, [field]: raw }));
    hideAmountCheck(check);
    setFieldErrors(prev => {
      if (!prev[serverKey]) return prev;
      const next = { ...prev };
      delete next[serverKey];
      return next;
    });
  };

  const handleSave = async () => {
    if (checkingConflicts) {
      setSaveError('Trwa weryfikacja dostępności sprzętu. Poczekaj na wynik.');
      scrollFormToTop();
      return;
    }
    if (conflicts.length > 0) {
      setSaveError('Usuń konfliktujące pozycje lub zmień zakres dat.');
      scrollFormToTop();
      return;
    }

    setSaving(true);
    try {
      const toDateObj = v => v instanceof Date ? v : (v ? parseYMD(v) : null);
      const newStartRaw = toDateObj(editForm.start_date);
      const newEndRaw = toDateObj(editForm.end_date);
      const origStartRaw = reservation && reservation.start_date ? parseYMD(reservation.start_date) : null;
      const origEndRaw = reservation && reservation.end_date ? parseYMD(reservation.end_date) : null;

      const normalizeDay = d => d ? new Date(d.getFullYear(), d.getMonth(), d.getDate()) : null;
      const newStart = normalizeDay(newStartRaw);
      const newEnd = normalizeDay(newEndRaw);
      const origStart = normalizeDay(origStartRaw);
      const origEnd = normalizeDay(origEndRaw);

      const startOfToday = getStartOfToday();
      if (newStart && newStart < startOfToday && !(origStart && newStart.getTime() === origStart.getTime())) {
        toast.error(t('reservationForm.date.startBeforeToday'));
        setSaving(false);
        return;
      }
      if (newEnd && newEnd < startOfToday && !(origEnd && newEnd.getTime() === origEnd.getTime())) {
        toast.error(t('reservationForm.date.endBeforeToday'));
        setSaving(false);
        return;
      }

      const start_date = newStart ? formatYMD(newStart) : null;
      const end_date   = newEnd   ? formatYMD(newEnd)   : null;

      const normId = v => {
        if (v == null) return null;
        if (typeof v === 'number') return v;
        if (typeof v === 'string' && /^\d+$/.test(v)) return Number(v);
        return v;
      };
      const normalizedEquipIds = Array.isArray(editForm.equipment_ids)
        ? editForm.equipment_ids.map(normId).filter(i => i != null)
        : (Array.isArray(editForm.equipment) ? editForm.equipment.map(normId).filter(i => i != null) : []);

      // NOWE: nie zapisujemy jeśli brak wybranego sprzętu
      if (!normalizedEquipIds || normalizedEquipIds.length === 0) {
        setSaveError('Wybierz przynajmniej jeden sprzęt, aby zapisać rezerwację.');
        setSaving(false);
        return;
      }

      // Limity kwot (U.26): pola niczego nie przycinają w trakcie pisania, więc tu jest ostatnia
      // kontrola przed wysyłką (`discountCode`/`advanceCode` liczone wyżej z bieżących wartości).
      // Wartość, która przestała się mieścić po usunięciu sztuki albo skróceniu terminu, przyciął
      // już efekt dopasowania, więc w praktyce trafia tu tylko to, co ktoś wpisał ponad limit.
      // Komunikaty pokazują pola i `amountsBanner` u góry okna.
      if (discountCode || advanceCode) {
        showAmountChecks();
        setSaveError('');
        setSaving(false);
        scrollFormToTop();
        return;
      }
      // Kwota rezerwacji tylko z ceny ostatecznej (27.10.2026): zapisanej przy niezmienionym sprzęcie
      // i datach albo z wyceny, która wróciła. Do jej powrotu panel liczy cenę bez cen dynamicznych
      // i zestawów, a zapis tuż po zmianie sprzętu zapisywał ją jako kwotę rezerwacji. Bez dat nie ma
      // czego wyceniać (o brakującej dacie mówi serwer), więc wtedy zapis nie czeka.
      if (!priceSettled && start_date && end_date) {
        // N.6: zapytanie o klasy bez wyceny z serwera: zła kwota (bez sztuk klas) nie może pójść do zapisu.
        setSaveError(komunikatWycenyKlas || t('reservation.quotePending'));
        setSaving(false);
        return;
      }
      const discount = amountOrZero(editForm.discount_value);

      const payload = {
        ...editForm,
        equipment: normalizedEquipIds,
        equipment_ids: normalizedEquipIds,
        discount,
        // Kontrakt bez zmian: liczby, nie tekst z pola (`...editForm` niesie surowy tekst).
        discount_value: discount,
        discount_unit: discountUnit,
        start_date,
        end_date,
        // Kwota PRZED rabatem, jak przy tworzeniu rezerwacji: `payment.amount` czytają podgląd,
        // lista kalendarza, statystyki i walidacja rabatu w PLN, i każde z nich odejmuje rabat
        // samo. Kwota po rabacie odejmowała go przy każdym zapisie od nowa (1000 → 900 → 810).
        total_price: pricing.subtotal,
        // Potwierdzenie dla serwera, że `total_price` jest PRZED rabatem (migracja 052): z nim
        // serwer zdejmuje znacznik „należność z cennika”, gdy kwota różni się od zapisanej. Bez
        // niego, przy rabacie, znacznik włącza, bo tak pisze karta otwarta przed wdrożeniem.
        amount_before_discount: true,
        deposit: pricing.deposit,
        advance_amount: amountOrZero(editForm.advance_amount),
        // P3.15 follow-up: NIE wysyłamy advance_paid — backend ignoruje (flaga wynika z płatności).
        // parseAmount, nie parseFloat: „150,50” dawało 150 (parseFloat kończy na przecinku).
        deposit_override: editForm.deposit_override_raw === '' ? null : parseAmount(editForm.deposit_override_raw)
      };

      // P3.31: kaucja to BLOKADA NA KARCIE, więc literówka nie kończy się brzydką liczbą, tylko
      // próbą zamrożenia prawdziwych pieniędzy klienta. Dwa realne scenariusze: dodatkowe zero
      // i kwota wpisana w groszach. Zauważone przy klikaniu 2026-08-06, gdy rezerwacja miała
      // ustawione 50 000 zł nadpisania przy sprzęcie z kaucją 150 zł.
      //
      // OSTRZEŻENIE, NIE LIMIT: przy drogim sprzęcie wysoka kaucja bywa uzasadniona, a każda
      // sztywna granica komuś przeszkodzi. Pytamy tylko wtedy, gdy kwota jest nieproporcjonalna
      // do sprzętu, i pozwalamy potwierdzić.
      const kaucjaWpisana = editForm.deposit_override_raw === '' ? null : parseAmount(editForm.deposit_override_raw);
      const kaucjaSprzetu = Number(pricing.deposit || 0);
      const PROG = 3;
      if (kaucjaWpisana != null && Number.isFinite(kaucjaWpisana) && kaucjaSprzetu > 0
          && kaucjaWpisana > kaucjaSprzetu * PROG) {
        const ile = Math.round(kaucjaWpisana / kaucjaSprzetu);
        const potwierdzone = await confirm({
          title: t('reservationForm.deposit.overrideConfirm', {
            amount: formatMoney(kaucjaWpisana, { locale: currentLanguage }),
            times: ile,
            equipmentDeposit: formatMoney(kaucjaSprzetu, { locale: currentLanguage }),
          }),
          tone: 'primary',
        });
        if (!potwierdzone) { setSaving(false); return; }
      }

      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/reservation/${reservation.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 409) {
          setConflicts(data.conflicts || []);
          setSaveError(data.message || 'Wybrany sprzęt jest już zarezerwowany w tym terminie.');
          scrollFormToTop();
        } else {
          setSaveError(data.message || 'Nie udało się zapisać rezerwacji.');
          // Parse error message to highlight fields
          const msg = (data.message || '').toLowerCase();
          const errors = {};
          if (/rabat|discount/.test(msg)) errors.discount = true;
          if (/zaliczka|advance/.test(msg)) errors.advanceAmount = true;
          if (/kaucja|deposit/.test(msg)) errors.depositOverride = true;
          if (/data|date/.test(msg)) errors.dateRange = true;
          setFieldErrors(errors);
        }
        scrollFormToTop();
        return;
      }
      setFieldErrors({});
      setIsEditing(false);
      if (onSave) await onSave();
    } catch (error) {
      console.error('Error saving reservation:', error);
      setSaveError('Wystąpił błąd podczas zapisywania zmian.');
      scrollFormToTop();
    } finally {
      setSaving(false);
    }
  };

  const days = daysInclusive(reservation.start_date, reservation.end_date);
  const equipmentItems = reservation.equipment_names && reservation.equipment_names !== 'Brak przypisanego sprzętu' 
    ? reservation.equipment_names.split(',').map(item => item.trim())
    : [];

  const formatValue = (value, fallback) => (value && String(value).trim() ? value : (fallback ?? t('common.noData')));

  const daysDisplay = isEditing ? (pricing?.days ?? days) : days;
  const subtotalDisplay = isEditing ? (pricing?.subtotal ?? 0) : (Number(reservation.subtotal || 0) || 0);
  const discountDisplay = isEditing ? (pricing?.discountAmount ?? 0) : (Number(reservation.discount_amount || 0) || 0);
  const totalDisplay = isEditing ? (pricing?.total ?? 0) : (Number(reservation.total_price || 0) || 0);
  const depositDisplay = isEditing
    ? (pricing?.deposit ?? 0)
    : (Number(reservation.deposit_override ?? reservation.deposit ?? 0) || 0);


  let DISPLAY_VALUES = {};
  if (!isEditing) {
    const daysNonEdit = days;
    const isLongTermNonEdit = daysNonEdit > 7;
    // Reguła domyślna także u firmy z własną tabelą: to wycena rezerwacji BEZ zapisanej kwoty, a tabela
    // działa tylko przy nowej wycenie (N.3).
    const billableDaysNonEdit = dniPlatne(daysNonEdit, null);

    const unit = String(reservation.discount_unit || '%').toUpperCase();
    const discVal = Number((reservation.discount ?? reservation.discount_amount ?? reservation.discount_value) || 0) || 0;

    // U.8 (RES-01/RES-07): read-mode PREFERUJE zapisaną kwotę rezerwacji. Zapisany
    // total_price zawiera korekty reguł cenowych (price rules) policzone przy rezerwacji,
    // spójnie z listą kalendarza (calendar/utils.js#calculateFinalPrice). Bez zapisanej kwoty
    // cena z wyceny serwera (`podgladKwota`, N.6), a lokalne przeliczenie getEquipmentDailyRate×dni
    // z detailEquipment (bez reguł cenowych i zestawów) zostaje TYLKO do jej powrotu, po jej błędzie
    // i dla rezerwacji ze znacznikiem „należność z cennika”.
    const hasDetails = Array.isArray(detailEquipment) && detailEquipment.length > 0;
    let subtotalFromReservation = 0;
    if (zapisanaKwota > 0) {
      subtotalFromReservation = zapisanaKwota;
    } else if (podgladKwota != null) {
      subtotalFromReservation = podgladKwota;
    } else if (hasDetails) {
      const dailySum = detailEquipment.reduce(
        (sum, eq) => sum + getEquipmentDailyRate(eq, isLongTermNonEdit),
        0
      );
      subtotalFromReservation = roundToCents(dailySum * billableDaysNonEdit);
    }

    // Rabat w pełnych groszach i kwota po rabacie tą samą regułą co faktura i link do zapłaty
    // (utils/amountField.js#discountInPln, N.6). Dawniej odejmowany bez zaokrąglenia: 50,10 zł minus 5%
    // pokazywało 47,60 zł przy fakturze na 47,59 zł.
    const discountAmtCurrency = discountInPln(discVal, unit === '%' ? '%' : 'PLN', subtotalFromReservation);

    const totalComputed = Math.max(0, Math.round(subtotalFromReservation * 100) - Math.round(discountAmtCurrency * 100)) / 100;
    const advanceAmt = Number(reservation.advance_amount || 0);
    const advancePaid = !!reservation.advance_paid;

    // kaucja: jeśli mamy szczegóły sprzętu licz jako suma bail (bez mnożenia przez dni)
    const depositComputed = (() => {
      const override = reservation.deposit_override;
      if (override != null) return Number(override) || 0; // priorytet override ZAWSZE
      if (hasDetails) {
        return detailEquipment.reduce((s, eq) => s + (Number(eq.bail || 0)), 0); // bez * billableDaysNonEdit
      }
      return Number(reservation.deposit ?? reservation.total_bail ?? 0) || 0;
    })();

    const effectiveToPay = Math.max(0, totalComputed - (advancePaid ? advanceAmt : 0));

    DISPLAY_VALUES = {
      days: daysNonEdit,
      subtotal: subtotalFromReservation,
      discountAmount: discountAmtCurrency,
      discountUnit: unit,
      discountValueRaw: discVal,
      total: totalComputed,
      deposit: depositComputed,
      effectiveToPay,                 // NOWE / spójne pole
      advance_amount: advanceAmt,     // NOWE
      advance_paid: advancePaid       // NOWE
    };
  } else {
    DISPLAY_VALUES = {
      days: pricing.days,
      subtotal: pricing.subtotal,
      discountAmount: pricing.discountAmount,
      discountUnit: editForm.discount_unit || '%',
      discountValueRaw: amountOrZero(editForm.discount_value),
      total: pricing.total,
      deposit: editForm.deposit_override_raw !== ''
        ? (parseAmount(editForm.deposit_override_raw) || 0)
        : pricing.deposit,
      effectiveToPay: pricing.effectiveToPay,
      advance_amount: amountOrZero(editForm.advance_amount),
      advance_paid: reservation.advance_paid // P3.15 follow-up: realny status z płatności, nie z pola
    };
  }

  // Dni płatne w podsumowaniu (N.3). Nowa wycena mówi je sama, a rezerwację bez zapisanej kwoty liczy
  // reguła domyślna. Przy zapisanej kwocie nie wiadomo, jaką tabelą ją policzono, bo przy rezerwacji się
  // tego nie zapisuje, więc firma z własną tabelą widzi samą liczbę dni, a nie liczbę, która może się
  // nie zgadzać z kwotą.
  const dniPlatneWPodsumowaniu = isEditing && !pricing?.savedAmount
    ? pricing?.billableDays
    : (tabelaDniPlatnych && zapisanaKwota > 0 ? null : dniPlatne(DISPLAY_VALUES.days, null));

  // „Zaliczka" tylko gdy wpłata online jest CZĘŚCIĄ kwoty — przy 100% to pełna płatność
  // (widget domyślnie pobiera całość i etykieta „zaliczka" wprowadzała w błąd).
  const advanceIsFull = Number(DISPLAY_VALUES.total || 0) > 0 &&
    Number(DISPLAY_VALUES.advance_amount || 0) >= Number(DISPLAY_VALUES.total || 0);
  const advanceLabel = advanceIsFull ? 'Płatność online' : 'Zaliczka';
  // P3.7: „Pozostało do zapłaty" uwzględnia dopłaty online (type=balance, netto po zwrotach).
  // 12.10.2026 (test odbiorczy płatności): zaliczka liczy się z rejestru płatności, tak jak należność
  // na serwerze (link, mail, Stripe). Flaga advance_paid × kwota zaliczki tylko wtedy, gdy rezerwacja
  // nie ma żadnego rozliczonego wpisu zaliczki (dawny ręczny znacznik) albo rejestr się nie wczytał.
  const zaliczkaWplaconaZl = zaliczkiZRejestru && zaliczkiZRejestru.liczba > 0
    ? Math.max(0, zaliczkiZRejestru.grosze) / 100
    : (reservation.advance_paid ? Number(reservation.advance_amount || 0) : 0);
  const remainingAfterOnline = Math.max(0, Math.round(
    (Number(DISPLAY_VALUES.total || 0) - zaliczkaWplaconaZl - balancePaidGrosze / 100) * 100,
  ) / 100);
  // Która sekcja prośby o płatność jest widoczna w podglądzie: zaliczka nieopłacona, dopłata po
  // opłaconej zaliczce albo całość bez zaliczki (null = żadna). Jedno źródło dla renderu sekcji
  // i dla stanu przycisków wysyłki, który po zmianie sekcji jest zapominany (patrz `wysylka`).
  const zaliczkaRezerwacji = Number(reservation.advance_amount || 0);
  const sekcjaPlatnosci = !reservation.advance_paid && zaliczkaRezerwacji > 0
    ? 'advance'
    : (reservation.advance_paid && remainingAfterOnline > 0
      ? 'balance'
      : (!reservation.advance_paid && zaliczkaRezerwacji === 0 && remainingAfterOnline > 0 ? 'full' : null));
  useEffect(() => {
    setWysylka((cur) => (cur.sekcja === sekcjaPlatnosci ? cur : BEZ_STANU_PRZYCISKU));
    setKopiowanie((cur) => (cur.sekcja === sekcjaPlatnosci ? cur : BEZ_STANU_PRZYCISKU));
  }, [sekcjaPlatnosci]);

  // P3.10: rejestracja płatności OFFLINE (gotówka/przelew). advance = planowana zaliczka
  // (spójnie z online), balance = kwota wpisana przez pracownika. Po zapisie onSave()
  // odświeża rezerwację w rodzicu (advance_paid) i przelicza dopłatę (efekt zależy od `reservation`).
  const recordOfflinePayment = async (type, method) => {
    // Guard SYNCHRONICZNY (ref, nie stan) — chroni przed podwójnym kliknięciem podczas
    // wolnej sieci: dla dopłaty nie ma ochrony 409 jak przy zaliczce, więc 2 klik = 2 wpisy.
    if (offlineBusyRef.current) return;
    let amountZl;
    if (type === 'balance') {
      const input = window.prompt(t('reservationForm.offline.amountPrompt', { how: slowoMetody(method, t) }), kwotaDoPola(remainingAfterOnline));
      if (input === null) return;
      amountZl = Number(String(input).replace(',', '.'));
      if (!(amountZl > 0)) { toast.error(t('reservationForm.offline.invalidAmount')); return; }
    }
    const what = type === 'advance'
      ? t('reservationForm.offline.advanceWord')
      : t('reservationForm.offline.paymentWord', { amount: formatMoney(amountZl, { locale: currentLanguage }) });
    const how = slowoMetody(method, t);
    const ok = await confirm({
      title: t('reservationForm.offline.confirmSaveTitle', { what, how }),
      message: t('reservationForm.offline.confirmSaveMessage'),
      tone: 'primary',
    });
    if (!ok) return;
    offlineBusyRef.current = true;
    setOfflineBusy(true);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const r = await fetch(`${API_BASE}/api/payments/record-offline`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
        body: JSON.stringify({ reservation_id: reservation.id, type, method, ...(type === 'balance' ? { amount_zl: amountZl } : {}) }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || t('reservationForm.offline.saveError'));
      if (typeof onSave === 'function') await onSave();
    } catch (err) {
      toast.error(err.message || t('reservationForm.offline.saveError'));
    } finally {
      offlineBusyRef.current = false;
      setOfflineBusy(false);
    }
  };
  const offlineBtnStyle = { flex: '1 1 auto', minWidth: 0, padding: '8px 10px', border: '1px solid var(--input-border)', borderRadius: 8, background: 'transparent', color: 'var(--text)', fontWeight: 600, cursor: offlineBusy ? 'wait' : 'pointer', fontSize: '0.8rem', opacity: offlineBusy ? 0.6 : 1 };
  // Kaucja przyjęta na miejscu (gotówka albo przelew, 12.10.2026; F.10: karta z terminala). Kwotę
  // bierze serwer z rezerwacji.
  const zapiszKaucjeNaMiejscu = async (method) => {
    if (offlineBusyRef.current) return;
    const how = slowoMetody(method, t);
    const ok = await confirm({
      title: t('reservationForm.deposit.offlineConfirm', {
        amount: formatMoney(Number(DISPLAY_VALUES.deposit || 0), { locale: currentLanguage }), how,
      }),
      tone: 'primary',
    });
    if (!ok) return;
    offlineBusyRef.current = true;
    setOfflineBusy(true);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const r = await fetch(`${API_BASE}/api/payments/record-offline`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
        body: JSON.stringify({ reservation_id: reservation.id, type: 'deposit', method }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || t('reservationForm.offline.saveError'));
      toast.success(t('reservationForm.deposit.offlineSaved'));
      wczytajPlatnosci();
    } catch (err) {
      toast.error(err.message || t('reservationForm.offline.saveError'));
    } finally {
      offlineBusyRef.current = false;
      setOfflineBusy(false);
    }
  };

  // P3.14: link płatności na DOWOLNĄ (częściową) kwotę — mode='custom'. Serwer przycina do należności.
  const sendCustomLink = async (send) => {
    const zl = Number(String(customAmountZl).replace(',', '.'));
    if (!(zl > 0)) { toast.error(t('reservationForm.offline.invalidAmount')); return; }
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const r = await fetch(`${API_BASE}/api/payments/send-link`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
        body: JSON.stringify({ reservation_id: reservation.id, send, mode: 'custom', amount_zl: zl }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok && pokazBladPremium(data)) return;
      if (!r.ok || (!send && !data.url)) throw new Error(data.error || t('reservationForm.payment.prepareLinkError'));
      poZdjeciuTerminu(data);
      if (send) toast.success(t('reservationForm.customLink.sentToClient'));
      else if (await copyTextToClipboard(data.url)) {
        toast.success(t('reservationForm.customLink.copied'));
        await potwierdzSkopiowanie(data.payment_id);
      }
      wczytajPlatnosci();
      setCustomAmountZl('');
    } catch (err) {
      toast.error(err.message || t('common.error'));
    }
  };

  // Generowanie umowy trwa kilka sekund (pod obciążeniem dłużej), a przycisk wyglądał przez ten
  // czas tak samo, więc drugie kliknięcie wysyłało drugie żądanie i drugi render na serwerze.
  // Ref blokuje powtórzenie od razu, stan zmienia napis i wyłącza przyciski (11.10.2026).
  const [generatingContract, setGeneratingContract] = useState(false);
  const generatingContractRef = useRef(false);
  const generateContractNow = async () => {
    if (generatingContractRef.current) return;
    generatingContractRef.current = true;
    setGeneratingContract(true);
    try {
      const data = buildContractDataFromCalendar({
        isEditing,
        // utils/contract.js czyta kwoty jako liczby, a formularz trzyma surowy tekst pól (U.26).
        editForm: {
          ...editForm,
          discount_value: amountOrZero(editForm.discount_value),
          advance_amount: amountOrZero(editForm.advance_amount),
        },
        reservation,
        availableEquipment,
        detailEquipment,
        pricing,
        DISPLAY_VALUES,
        tabelaDniPlatnych
      });
      await sendAndDownloadContract(data, { t });
      toast.success(t('reservation.contractGenerated'));
    } catch (e) {
      toast.error(t('reservationForm.contract.generateError', { message: e?.message || String(e) }));
    } finally {
      generatingContractRef.current = false;
      setGeneratingContract(false);
    }
  };

  const editingEquipmentLongTerm = (pricing?.days || 0) > 7;
  const nonEditingEquipmentLongTerm = (DISPLAY_VALUES?.days || 0) > 7;

  // P7.5: price-visibility view-model — edit mode only (the quote effect runs only
  // while editing); null (no/stale quote) → UI identical to pre-P7.5.
  const editSelectedEq = isEditing
    ? (availableEquipment || []).filter(eq => Array.isArray(editForm.equipment_ids) && editForm.equipment_ids.includes(eq.id))
    : [];
  const editQuoteKey = quoteKey(editForm.equipment_ids, formatYMD(editForm.start_date), formatYMD(editForm.end_date));
  // Przy kwocie zapisanej rozbicie z wyceny innej niż ta kwota opisywałoby inną cenę: wiersze nie
  // sumowałyby się do „Cena sprzętu”, a ramka zestawu podawałaby stawkę, której rezerwacja nie ma.
  // Wycena równa zapisanej kwocie zostaje, a po zmianie sprzętu lub dat kwota i tak idzie z wyceny.
  const quoteOpisujeCene = !!priceQuote && priceQuote.key === editQuoteKey
    && (!pricing?.savedAmount || Math.abs(Number(priceQuote.data?.grandTotal) - pricing.subtotal) < 0.005);
  const editQuoteData = (isEditing && quoteOpisujeCene) ? priceQuote.data : null;
  const priceBreakdown = buildPriceBreakdown(editQuoteData, editSelectedEq, eq => getEquipmentDailyRate(eq, editingEquipmentLongTerm));
  const equipmentGroups = groupEquipmentForDisplay(editSelectedEq, priceBreakdown);
  const itemView = (eq) => (priceBreakdown ? priceBreakdown.itemsById[eq.id] : null);


  // ── Podgląd rezerwacji (przebudowa czytelności, 2026-10-02) ────────────────────────────
  // Fragmenty niżej to DOSŁOWNIE przeniesione gałęzie podglądu z dawnego formularza: logika
  // płatności, kaucji i zdjęć się nie zmieniła, zmienił się układ (reservationDetails.styles.js).
  // Rezerwacja ze znacznikiem „kwotę liczy cennik” (migracja 052, test odbiorczy płatności 12.10.2026).
  // Podgląd celowo liczy z zapisanej kwoty (PR #202), ale link, mail i płatność kartą liczą należność
  // z cennika, więc przycisk z kwotą z podglądu obiecywał 210 zł, a link pobierał 240 zł. Przy znaczniku
  // przycisk nie podaje kwoty, a uwaga mówi, skąd różnica i gdzie kwotę widać.
  // Status: ta sama reguła co lista kalendarza (withStatus w pages/calendar/index.js). Okno
  // otwarte z LINKU (Strona główna, Płatności, kalendarz sprzętu) dostaje wiersz bez `_status`,
  // więc bez tego nie miało ani plakietki statusu, ani przycisku „Usuń rezerwację".
  // X.1: z panelu wydania, gdy już się wczytał (lista w kalendarzu bywa sprzed ostatniego skanu).
  const statusRezerwacji = reservationStatusCode(handoverItems
    ? {
      ...reservation,
      equipment: handoverItems.map((i) => i.equipment_id),
      picked_up_equipment: handoverItems.filter((i) => i.picked_up_at).map((i) => i.equipment_id),
      returned_equipment: handoverItems.filter((i) => i.returned_at).map((i) => i.equipment_id),
    }
    : reservation);
  const kwotaZCennika = !!reservation.amount_needs_price_list;
  const etykietaLinkuReszty = kwotaZCennika
    ? t('reservationForm.payment.sendLinkPriceList')
    : `Wyślij link (${kwotaZl(remainingAfterOnline)})`;
  const uwagaCennika = kwotaZCennika ? t('reservationForm.payment.priceListNote') : null;

  const renderPlatnoscPodglad = () => (
    <>
                  <FormInputWrapper>
                    <FormLabel><CreditCardIcon /> {advanceLabel}</FormLabel>
                    <FormValue>
                      {Number(reservation.advance_amount || 0) > 0
                        ? `${kwotaZl(Number(reservation.advance_amount))} ${reservation.advance_paid ? '(opłacona)' : '(nieopłacona)'}`
                        /* Zaliczka 0 oznaczona jako oplacona zdarza sie po konwersji oplaconej
                           oferty na rezerwacje: samo „Bez zaliczki" gubilo wtedy informacje,
                           ze pieniadze juz wplynely, i obsluga widziala rezerwacje jak nowa. */
                        : (reservation.advance_paid ? 'Zaliczka opłacona' : 'Bez zaliczki')}
                    </FormValue>
                    {/* Wplaty typu balance (dopłata albo CAŁOŚĆ przy rezerwacji bez zaliczki) nie sa
                        widoczne w polu zaliczki, wiec w pelni oplacona rezerwacja wygladala na
                        nieoplacona. Pokazujemy, ile realnie wplynelo — BEZ slowa „online", bo ta
                        suma obejmuje takze gotowke i przelew przyjete przy ladzie (provider
                        'offline'), a modal zwrotu oddaje online tylko wplaty Stripe. */}
                    {balancePaidGrosze > 0 && (
                      <div style={{ marginTop: 6, fontSize: '0.9rem', color: 'var(--success)', fontWeight: 600 }}>
                        Zapłacono: {kwotaZl(balancePaidGrosze / 100)}
                      </div>
                    )}
                    {sekcjaPlatnosci === 'advance' && (
                      <PaymentDeadlineInfo
                        reservationId={reservation.id}
                        termin={terminWidoczny}
                        bezEmaila={!String(reservation.email || '').trim()}
                        oknoWidgetu={reservation.widget_cancel_hours}
                        onRemoved={() => setTerminLokalny({ rezerwacja: reservation.id, wartosc: null })}
                      />
                    )}
                    {sekcjaPlatnosci === 'advance' && renderSekcjaPlatnosci({
                      mode: undefined,
                      etykietaWyslij: 'Wyślij klientowi link do zapłaty',
                      etykietaKopiuj: 'Kopiuj link do zapłaty',
                      tytulKarty: 'Klient płaci teraz, na tym urządzeniu (np. przy kasie)',
                      typOffline: 'advance',
                      pokazRate: false,
                    })}
                    {/* P3.7: zaliczka opłacona, ale coś zostało — link na POZOSTAŁĄ kwotę
                        (ten sam token; publiczny handler sam liczy resztę serwerowo). */}
                    {sekcjaPlatnosci === 'balance' && renderSekcjaPlatnosci({
                      mode: 'balance',
                      etykietaWyslij: etykietaLinkuReszty,
                      etykietaKopiuj: 'Kopiuj link',
                      tytulKarty: 'Klient płaci kartą teraz, na tym urządzeniu',
                      typOffline: 'balance',
                      pokazRate: true,
                      uwaga: uwagaCennika,
                    })}
                    {/* P3.12: rezerwacja BEZ zaliczki i nieopłacona — pobór CAŁOŚCI (link/kasa/offline).
                        Domyka lukę: przy advance_amount=0 sekcje zaliczki i dopłaty się nie renderują. */}
                    {sekcjaPlatnosci === 'full' && (
                      <>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Do zapłaty: {kwotaZl(remainingAfterOnline)} (bez zaliczki)</div>
                        {renderSekcjaPlatnosci({
                          mode: 'full',
                          etykietaWyslij: etykietaLinkuReszty,
                          etykietaKopiuj: 'Kopiuj link',
                          tytulKarty: 'Klient płaci kartą teraz, na tym urządzeniu',
                          typOffline: 'balance',
                          pokazRate: true,
                          uwaga: uwagaCennika,
                        })}
                      </>
                    )}
                  </FormInputWrapper>
    </>
  );

  const renderKaucjaPodglad = () => (
    <>
                  {/* P3.8: kaucja online — blokada na karcie (bez pobrania), ściągnięcie
                      przy szkodzie, zwolnienie przy zwrocie. Stan z payments (type=deposit). */}
                  <FormInputWrapper>
                    <FormLabel><CreditCardIcon /> {t('reservationForm.deposit.title')}</FormLabel>
                    <FormValue>
                      {depositPayment && depositPayment.provider === 'offline'
                        ? (() => {
                          const amount = formatMoney(Number(depositPayment.captured_grosze || depositPayment.amount_grosze) / 100, { locale: currentLanguage });
                          if (depositPayment.method === 'transfer') return t('reservationForm.deposit.takenTransfer', { amount });
                          if (depositPayment.method === 'card') return t('reservationForm.deposit.takenCard', { amount });
                          return t('reservationForm.deposit.takenCash', { amount });
                        })()
                        : depositPayment
                        ? (depositPayment.status === 'authorized'
                            ? `${kwotaZl(Number(depositPayment.amount_grosze) / 100)} (zablokowana na karcie)`
                            : (depositPayment.status === 'captured' || depositPayment.status === 'partially_refunded')
                              ? `ściągnięto ${kwotaZl(Number(depositPayment.captured_grosze) / 100)} z ${kwotaZl(Number(depositPayment.amount_grosze) / 100)}`
                              : `${kwotaZl(Number(depositPayment.amount_grosze) / 100)} (link czeka na klienta)`)
                        : `${kwotaZl(Number(DISPLAY_VALUES.deposit || 0))} (brak blokady)`}
                    </FormValue>
                    {/* Los RESZTY blokady po ściągnięciu części. Stripe przy częściowym pobraniu
                        zwalnia niepobraną kwotę SAM, od razu, i nie da się jej już później dobrać.
                        Bez tego zdania „ściągnięto 60 zł z 150 zł" czyta się tak, jakby 90 zł
                        nadal wisiało na karcie, a wtedy „Zwróć kaucję" wygląda na przycisk do ich
                        zwolnienia, którym nie jest (oddaje pobrane 60 zł). Zapytał o to Piotrek
                        2026-08-04 przy pierwszym ściągnięciu kaucji na żywym Stripe. */}
                    {depositPayment && depositPayment.provider !== 'offline' && ['captured', 'partially_refunded'].includes(depositPayment.status)
                      && Number(depositPayment.amount_grosze) > Number(depositPayment.captured_grosze || 0) && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4, maxWidth: 320 }}>
                        Pozostałe {kwotaZl((Number(depositPayment.amount_grosze) - Number(depositPayment.captured_grosze || 0)) / 100)}
                        {' '}zwolnione z karty. Blokady nie da się ściągnąć drugi raz.
                      </div>
                    )}
                    {(!depositPayment || !['captured', 'partially_refunded'].includes(depositPayment.status)) && Number(DISPLAY_VALUES.deposit || 0) > 0 && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4, maxWidth: 320 }}>
                        Blokada na karcie, nie pobranie. Odrębna od opłaty za wypożyczenie: ściągasz przy szkodzie, zwalniasz przy zwrocie.
                      </div>
                    )}
                    {/* Anulowana rezerwacja nie przyjmuje nowej kaucji (12.10.2026): serwer odpowiada 409,
                        więc przyciski nie mają sensu. Zostaje zwolnienie i zwrot już przyjętej. */}
                    {statusRezerwacji === 'cancelled' && (!depositPayment || depositPayment.status === 'pending') && Number(DISPLAY_VALUES.deposit || 0) > 0 && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4, maxWidth: 320 }} data-cy="deposit-cancelled-note">
                        {t('reservationForm.deposit.cancelledNote')}
                      </div>
                    )}
                    {statusRezerwacji !== 'cancelled' && (!depositPayment || depositPayment.status === 'pending') && Number(DISPLAY_VALUES.deposit || 0) > 0 && (
                      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 320 }}>
                        <SladWysylki platnosc={depositPayment} />
                        {!linkiOnline && (
                          <PremiumNotice feature="payment_links" compact data-cy="deposit-links-premium">
                            {t('reservationForm.deposit.linksPremium')}
                          </PremiumNotice>
                        )}
                        <fieldset disabled={!linkiOnline} style={stylBlokadyLinkow} data-cy="deposit-links-lock">
                        {reservation.email && (
                          <button
                            type="button"
                            disabled={depositBusy}
                            style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--primary)', borderRadius: 8, background: 'transparent', color: 'var(--primary)', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}
                            onClick={async () => {
                              const out = await depositAction('/api/payments/deposit-link', { reservation_id: reservation.id, send: true });
                              poZdjeciuTerminu(out);
                              if (out) toast.success(t('reservationForm.deposit.linkSent'));
                            }}
                          >
                            Wyślij link do kaucji
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={depositBusy}
                          style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--input-border)', borderRadius: 8, background: 'transparent', color: 'var(--text)', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}
                          onClick={async () => {
                            const out = await depositAction('/api/payments/deposit-link', { reservation_id: reservation.id, send: false });
                            poZdjeciuTerminu(out);
                            if (out && out.url && await copyTextToClipboard(out.url)) {
                              toast.success(t('reservationForm.deposit.linkCopied'));
                              await potwierdzSkopiowanie(out.payment_id);
                            }
                            wczytajPlatnosci();
                          }}
                        >
                          Kopiuj link do kaucji
                        </button>
                        </fieldset>
                        {/* Kaucja przyjęta NA MIEJSCU (12.10.2026): gotówka albo przelew. Kwotę ustala
                            serwer z rezerwacji; oddaje się ją przyciskiem „Oddaj kaucję” niżej. */}
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('reservationForm.deposit.offlineHint')}</div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button type="button" style={offlineBtnStyle} data-cy="deposit-offline-cash" onClick={() => zapiszKaucjeNaMiejscu('cash')} disabled={offlineBusy || depositBusy}>{t('reservationForm.deposit.offlineCash')}</button>
                          <button type="button" style={offlineBtnStyle} data-cy="deposit-offline-transfer" onClick={() => zapiszKaucjeNaMiejscu('transfer')} disabled={offlineBusy || depositBusy}>{t('reservationForm.deposit.offlineTransfer')}</button>
                          <button type="button" style={offlineBtnStyle} data-cy="deposit-offline-card" onClick={() => zapiszKaucjeNaMiejscu('card')} disabled={offlineBusy || depositBusy}>{t('reservationForm.deposit.offlineCard')}</button>
                        </div>
                      </div>
                    )}
                    {depositPayment && depositPayment.status === 'authorized' && (
                      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 320 }}>
                        <button
                          type="button"
                          disabled={depositBusy}
                          style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--danger)', borderRadius: 8, background: 'transparent', color: 'var(--danger)', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}
                          onClick={async () => {
                            const maxZl = Number(depositPayment.amount_grosze) / 100;
                            const input = window.prompt(`Kwota do ściągnięcia w zł (maksymalnie ${kwotaDoPola(maxZl)}):`, kwotaDoPola(maxZl));
                            if (input === null) return;
                            const amount = Math.round(Number(String(input).replace(',', '.')) * 100);
                            if (!Number.isInteger(amount) || amount < 1 || amount > Number(depositPayment.amount_grosze)) {
                              toast.error(t('reservationForm.deposit.invalidAmountRange', { max: formatMoney(maxZl, { locale: currentLanguage }) }));
                              return;
                            }
                            // P5.3: powód trafia do logu płatności i do dokumentacji zwrotu.
                            const reason = window.prompt('Powód potrącenia (opcjonalnie, trafi do dokumentacji):', '') || '';
                            const out = await depositAction(
                              `/api/payments/${depositPayment.id}/capture-deposit`,
                              { amount_grosze: amount, reason: reason.trim() },
                              t('reservationForm.deposit.captureConfirm', { amount: formatMoney(amount / 100, { locale: currentLanguage }) })
                            );
                            if (out) {
                              setDepositPayment({ ...depositPayment, status: 'captured', captured_grosze: amount });
                              loadPhotos();
                            }
                          }}
                        >
                          Ściągnij z kaucji
                        </button>
                        <button
                          type="button"
                          disabled={depositBusy}
                          style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--input-border)', borderRadius: 8, background: 'transparent', color: 'var(--text)', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}
                          onClick={async () => {
                            const out = await depositAction(
                              `/api/payments/${depositPayment.id}/release-deposit`,
                              {},
                              t('reservationForm.deposit.releaseConfirm')
                            );
                            if (out) setDepositPayment(null);
                          }}
                        >
                          Zwolnij kaucję
                        </button>
                      </div>
                    )}
                    {/* P3.9: zwrot ŚCIĄGNIĘTEJ kaucji z panelu (np. reklamacja) — do wysokości
                        pobranej kwoty pomniejszonej o wcześniejsze zwroty. Kaucję przyjętą na miejscu
                        oddaje także pracownik, zwrot przez Stripe tylko administrator (13.10.2026,
                        ta sama reguła co serwer, POST /api/payments/:id/refund). */}
                    {depositPayment && ['captured', 'partially_refunded'].includes(depositPayment.status)
                      && Number(depositPayment.captured_grosze || 0) > Number(depositPayment.amount_refunded_grosze || 0) && (
                      <div style={{ marginTop: 10, maxWidth: 320 }}>
                        {Number(depositPayment.amount_refunded_grosze || 0) > 0 && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 6 }}>
                            zwrócono {kwotaZl(Number(depositPayment.amount_refunded_grosze) / 100)}
                          </div>
                        )}
                        {depositPayment.provider !== 'offline' && !isAdminUser ? (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }} data-cy="deposit-refund-admin-only">
                            {t('reservationForm.deposit.refundAdminOnly')}
                          </div>
                        ) : (
                        <button
                          type="button"
                          disabled={depositBusy}
                          style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--danger)', borderRadius: 8, background: 'transparent', color: 'var(--danger)', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}
                          onClick={async () => {
                            const remaining = (Number(depositPayment.captured_grosze) - Number(depositPayment.amount_refunded_grosze || 0));
                            const maxZl = remaining / 100;
                            const input = window.prompt(`Kwota zwrotu kaucji w zł (maksymalnie ${kwotaDoPola(maxZl)}):`, kwotaDoPola(maxZl));
                            if (input === null) return;
                            const amount = Math.round(Number(String(input).replace(',', '.')) * 100);
                            if (!Number.isInteger(amount) || amount < 1 || amount > remaining) {
                              toast.error(t('reservationForm.deposit.invalidAmountRange', { max: formatMoney(maxZl, { locale: currentLanguage }) }));
                              return;
                            }
                            const out = await depositAction(
                              `/api/payments/${depositPayment.id}/refund`,
                              { amount_grosze: amount },
                              depositPayment.provider === 'offline'
                                ? t('reservationForm.deposit.giveBackConfirm', { amount: formatMoney(amount / 100, { locale: currentLanguage }) })
                                : t('reservationForm.deposit.refundConfirm', { amount: formatMoney(amount / 100, { locale: currentLanguage }), email: reservation.email || '' })
                            );
                            if (out) setDepositPayment({ ...depositPayment, status: out.status || 'partially_refunded', amount_refunded_grosze: out.refunded_grosze });
                          }}
                        >
                          {depositPayment.provider === 'offline' ? t('reservationForm.deposit.giveBack') : 'Zwróć kaucję'}
                        </button>
                        )}
                      </div>
                    )}
                  </FormInputWrapper>
    </>
  );

  const renderSprzetPodglad = () => (
            (Array.isArray(detailEquipment) && detailEquipment.length > 0) ? (
              <FormEquipmentList>
                {detailEquipment.map(equipment => (
                  <EquipmentItemRow key={equipment.id}>
                    <EquipmentInfo>
                      <PackageIcon />
                      <div>
                        <EquipmentName>{equipment.name}</EquipmentName>
                        <br />
                        <EquipmentMeta>
                          {equipment.inventory_number && `Nr: ${equipment.inventory_number} • `}{equipment.class} • {equipment.category} • Rozmiar: {equipment.size}
                        </EquipmentMeta>
                      </div>
                    </EquipmentInfo>
                   <EquipmentActions>
                     <EquipmentPrice>
                       {kwotaZl(getEquipmentDailyRate(equipment, nonEditingEquipmentLongTerm))}/dzień
                     </EquipmentPrice>
                   </EquipmentActions>
                  </EquipmentItemRow>
                ))}
              </FormEquipmentList>
            ) : equipmentItems.length > 0 ? (
              <FormEquipmentList>
                {equipmentItems.map((item, index) => (
                  <EquipmentItemRow key={index}>
                    <EquipmentInfo>
                      <PackageIcon />
                      <div><EquipmentName>{item}</EquipmentName></div>
                    </EquipmentInfo>
                   <EquipmentActions>
                     <EquipmentPrice> </EquipmentPrice>
                   </EquipmentActions>
                  </EquipmentItemRow>
                ))}
              </FormEquipmentList>
            ) : (
              <EmptyState>
                <div className="icon"><PackageIcon /></div>
                <div className="text">Brak przypisanego sprzętu</div>
              </EmptyState>
            )
  );

  const renderDokumentacja = () => (
          <FormPanel>
            <FormPanelHeader>
              <FormPanelIcon $tone="clay"><DocumentIcon /></FormPanelIcon>
              <FormPanelTitle>Dokumentacja stanu sprzętu</FormPanelTitle>
            </FormPanelHeader>

            {[['pickup', 'Wydanie'], ['return', 'Zwrot']].map(([ph, label]) => {
              const entries = photos.filter((p) => p.phase === ph);
              return (
                <div key={ph} data-cy={`photo-section-${ph}`} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>
                      {label}{entries.length ? ` (${entries.length})` : ''}
                    </span>
                    {ph === 'return' && (
                      <SecondaryButton
                        type="button"
                        data-cy="add-return-photos"
                        onClick={startReturnDocumentation}
                        style={{ padding: '4px 12px', fontSize: '0.8rem' }}
                      >
                        + Dodaj zdjęcia zwrotu
                      </SecondaryButton>
                    )}
                  </div>
                  {entries.length === 0 ? (
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Brak wpisów.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {entries.map((p) => (
                        <DocEntry key={p.id} style={{ alignItems: 'flex-start' }}>
                          {p.url && (
                            <a href={`${API_BASE}${p.url}`} target="_blank" rel="noopener noreferrer" title="Otwórz w pełnym rozmiarze">
                              <img src={`${API_BASE}${p.thumbUrl || p.url}`} alt="Dokumentacja sprzętu" loading="lazy" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, display: 'block', flexShrink: 0 }} />
                            </a>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {p.note && <div style={{ fontSize: '0.9rem', color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{p.note}</div>}
                            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4 }}>
                              {p.equipment_name ? `${p.equipment_name} · ` : ''}
                              {p.created_by_name ? `${p.created_by_name} · ` : ''}
                              {p.created_at ? new Date(p.created_at).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                            </div>
                          </div>
                          {isAdminUser && (
                            <DocRemove type="button" onClick={() => deletePhotoEntry(p.id)}>Usuń</DocRemove>
                          )}
                        </DocEntry>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <div ref={photoAddFormRef} style={{ paddingTop: 14, borderTop: '1px solid var(--input-border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <SimpleSelect
                  value={photoPhase}
                  onChange={setPhotoPhase}
                  disabled={photoBusy}
                  style={{ minWidth: 130 }}
                  options={[{ value: 'pickup', label: 'Wydanie' }, { value: 'return', label: 'Zwrot' }]}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <SimpleSelect
                    value={photoEquipmentId}
                    onChange={setPhotoEquipmentId}
                    disabled={photoBusy}
                    style={{ minWidth: 170 }}
                    options={[
                      { value: '', label: 'Cały zestaw' },
                      ...(Array.isArray(detailEquipment) ? detailEquipment : []).map((eq) => ({ value: String(eq.id), label: `${eq.name}${eq.inventory_number ? ` (${eq.inventory_number})` : ''}` })),
                    ]}
                  />
                  {detailEquipmentFailed > 0 && (
                    <div data-cy="equipment-load-warning" style={{ fontSize: '0.75rem', color: 'var(--danger)', maxWidth: 220, lineHeight: 1.35 }}>
                      Nie udało się wczytać {detailEquipmentFailed} {detailEquipmentFailed === 1 ? 'sztuki' : 'sztuk'} sprzętu, więc wybór per sztuka może być niepełny.{' '}
                      <button
                        type="button"
                        data-cy="equipment-load-retry"
                        onClick={loadDetailEquipment}
                        style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary)', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
                      >
                        Spróbuj ponownie
                      </button>
                    </div>
                  )}
                </div>
                {/* Na czas wysyłki wybór zdjęć i notatka są zablokowane: po wysyłce formularz dostaje
                    stan z chwili kliknięcia, więc zmiany wprowadzone w trakcie przepadały bez słowa
                    (przegląd 28.09.2026). */}
                <DocFileButton $disabled={photoBusy} aria-disabled={photoBusy || undefined}>
                  <CameraIcon />
                  {photoFiles.length ? `Wybrano: ${photoFiles.length}` : 'Wybierz zdjęcia'}
                  <input
                    ref={photoFileInputRef}
                    type="file"
                    data-cy="photo-file"
                    disabled={photoBusy}
                    accept="image/jpeg,image/png,image/webp"
                    // Bez `capture`: na iPhonie ten atrybut otwierał od razu aparat i nie dało się
                    // wybrać zdjęcia z galerii (sprawdzone na telefonie 27.09.2026). Bez niego iOS
                    // pokazuje wybór „Zrób zdjęcie / Biblioteka zdjęć”.
                    multiple
                    onChange={(e) => setPhotoFiles(e.target.files ? Array.from(e.target.files) : [])}
                  />
                </DocFileButton>
              </div>
              {/* FAZA T, T.4: dane osobowe na zdjęciu dowodowym to twarze, tablice i dokumenty. */}
              <div data-cy="photo-crop-hint" style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                {t('photos.cropHint')}
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <FormInput
                  type="text"
                  data-cy="photo-note"
                  placeholder="Notatka (opcjonalnie)"
                  value={photoNote}
                  onChange={(e) => setPhotoNote(e.target.value)}
                  disabled={photoBusy}
                  maxLength={2000}
                  style={{ flex: 1, minWidth: 220 }}
                />
                <SecondaryButton type="button" data-cy="photo-submit" onClick={addPhotoEntry} disabled={photoBusy}>
                  {photoBusy ? 'Zapisywanie…' : '+ Dodaj do dokumentacji'}
                </SecondaryButton>
              </div>
            </div>
          </FormPanel>
  );

  const renderTabelaPodsumowania = ({ bezPozostalo = false } = {}) => (
          <SummaryTable>
            <SummaryRow>
              <SummaryLabel>Liczba dni</SummaryLabel>
              <SummaryValue data-cy="details-days">
                {DISPLAY_VALUES.days}{dniPlatneWPodsumowaniu != null && ` ${t('pricing.paidDays', { days: pokazDniPlatne(dniPlatneWPodsumowaniu) })}`}
              </SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>Cena sprzętu</SummaryLabel>
              <SummaryValue data-cy="details-subtotal">
                {kwotaZl(Number(DISPLAY_VALUES.subtotal || 0))}
                <QuotePendingNote pending={isEditing ? quotePending : podgladWycenaTrwa} />
              </SummaryValue>
            </SummaryRow>
            {isEditing && komunikatWycenyKlas && (
              <SummaryRow>
                <span data-cy="class-quote-problem" role="alert" style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--danger)' }}>
                  {komunikatWycenyKlas}
                </span>
              </SummaryRow>
            )}
            {isEditing && (
              <SummaryBreakdownRows breakdown={priceBreakdown} Row={SummaryRow} Label={SummaryLabel} Value={SummaryValue} />
            )}
            {/* ZAWSZE POKAŻ RABAT (jeśli 0 -> pokaż 0.00 zł) */}
            <SummaryRow>
              <SummaryLabel>
                {`Rabat ${String(DISPLAY_VALUES.discountUnit || '%').toUpperCase() === '%'
                  ? `(${Number(DISPLAY_VALUES.discountValueRaw || 0)}%)`
                  : `(${kwotaZl(Number(DISPLAY_VALUES.discountValueRaw || 0))})`}`}
              </SummaryLabel>
              <SummaryValue>
                {Number(DISPLAY_VALUES.discountAmount || 0) > 0
                  ? `-${kwotaZl(Number(DISPLAY_VALUES.discountAmount))}`
                  : kwotaZl(0)}
              </SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>{advanceLabel}</SummaryLabel>
              <SummaryValue data-cy="details-advance">
                {Number(DISPLAY_VALUES.advance_amount || 0) > 0
                  ? `${kwotaZl(Number(DISPLAY_VALUES.advance_amount))} ${DISPLAY_VALUES.advance_paid ? '(opłacona)' : '(nieopłacona)'}`
                  : (DISPLAY_VALUES.advance_paid ? 'Zaliczka opłacona' : 'Bez zaliczki')}
              </SummaryValue>
            </SummaryRow>
            <SummaryRow style={{ color: 'var(--info)' }}>
              <SummaryLabel style={{ color: 'var(--info)' }}>Kaucja</SummaryLabel>
              <SummaryValue style={{ color: 'var(--info)' }}>
                {isEditing ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FormInput
                      type="text"
                      inputMode="decimal"
                      placeholder={kwotaDoPola(pricing.deposit)}
                      value={editForm.deposit_override_raw}
                      onChange={e => setEditForm(prev => ({ ...prev, deposit_override_raw: e.target.value }))}
                      style={{ width: 120 }}
                      data-error={!!fieldErrors.depositOverride}
                      data-cy="reservation-edit-deposit"
                    />
                    <span>PLN</span>
                    {editForm.deposit_override_raw !== '' && (
                      <button
                        type="button"
                        onClick={() => setEditForm(p => ({ ...p, deposit_override_raw: '' }))}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--text-muted)',
                          cursor: 'pointer',
                          fontSize: '0.75rem'
                        }}
                        title="Wyczyść nadpisanie"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ) : (
                  kwotaZl(Number(DISPLAY_VALUES.deposit || 0))
                )}
              </SummaryValue>
            </SummaryRow>
            {!bezPozostalo && (
            <SummaryRow>
              <SummaryLabel>Pozostało do zapłaty</SummaryLabel>
              <SummaryValue>
                {kwotaZl(remainingAfterOnline)}
                {balancePaidGrosze > 0 && (
                  <div style={{ fontWeight: 400, fontSize: '0.76rem', color: 'var(--text-muted)' }}>
                    {/* Bez slowa „online": ta sama suma co wyzej, wiec obejmuje takze gotowke
                        i przelew przyjete przy ladzie (provider 'offline'). */}
                    w tym dopłacono {kwotaZl(balancePaidGrosze / 100)}
                  </div>
                )}
              </SummaryValue>
            </SummaryRow>
            )}
          </SummaryTable>
  );

  const dniPodpis = (n) => {
    switch (new Intl.PluralRules(localeTag(currentLanguage)).select(n)) {
      case 'one': return t('reservationDetails.daysOne', { count: n });
      case 'few': return t('reservationDetails.daysFew', { count: n });
      case 'many': return t('reservationDetails.daysMany', { count: n });
      default: return t('reservationDetails.daysOther', { count: n });
    }
  };

  // Cały sprzęt wrócił, a kaucja wciąż wisi na karcie: podpowiedź, nie automat (P3.8).
  const kaucjaDoZwolnienia = Array.isArray(handoverItems) && handoverItems.length > 0
    && handoverItems.every((i) => i.returned_at) && depositPayment?.status === 'authorized';

  const renderPodglad = () => {
    const klient = [reservation.firstname, reservation.lastname].filter(Boolean).join(' ').trim();
    const adres = (() => {
      const parts = [];
      if (reservation.street) parts.push(reservation.street);
      if (reservation.building_number) parts.push(reservation.apartment_number ? `${reservation.building_number}/${reservation.apartment_number}` : reservation.building_number);
      const pcCity = [reservation.postal_code, reservation.city].filter(Boolean).join(' ');
      if (pcCity) parts.push(pcCity);
      return parts.join(', ');
    })();
    const kodStatusu = RESERVATION_STATUS_CODES.includes(statusRezerwacji) ? statusRezerwacji : null;
    const notatka = reservation.additional_note ? String(reservation.additional_note).trim() : '';
    const liczbaSztuk = (Array.isArray(detailEquipment) && detailEquipment.length > 0) ? detailEquipment.length : equipmentItems.length;
    const oplacona = remainingAfterOnline <= 0 && Number(DISPLAY_VALUES.total || 0) > 0;

    return (
      <>
        <DetailsHero>
          <DetailsEyebrow>{t('reservationDetails.eyebrow', { id: reservation.id })}</DetailsEyebrow>
          <DetailsTitleRow>
            <DetailsTitle>{klient || t('common.noData')}</DetailsTitle>
            {kodStatusu && (
              <StatusPill $fg={STATUS_STYLES[kodStatusu]?.fg} $bg={STATUS_STYLES[kodStatusu]?.bg} data-cy="details-status">
                {t(`calendar.status.${kodStatusu}`)}
              </StatusPill>
            )}
          </DetailsTitleRow>
          <DetailsWhen>
            {formatDate(reservation.start_date, { locale: currentLanguage })} → {formatDate(reservation.end_date, { locale: currentLanguage })}
            {' '}<span>· {dniPodpis(days)}</span>
          </DetailsWhen>
          {(reservation.phone || reservation.email || adres) && (
            <ContactRow>
              {reservation.phone && (
                <ContactChip href={`tel:${String(reservation.phone).replace(/\s+/g, '')}`}>
                  <PhoneIcon />{reservation.phone}
                </ContactChip>
              )}
              {reservation.email && (
                <ContactChip href={`mailto:${reservation.email}`}>
                  <MailIcon />{reservation.email}
                </ContactChip>
              )}
              {adres && (
                <ContactChip as="span">
                  <MapPinIcon />{adres}
                </ContactChip>
              )}
            </ContactRow>
          )}
        </DetailsHero>

        <DetailsGrid>
          <DetailsColumn>
            {/* Zapytanie z widgetu o sprzęt z klas (069): dobranie sztuk, potwierdzenie albo odmowa. */}
            {Number(reservation.class_units) > 0 && (
              <ClassRequestPanel
                reservationId={reservation.id}
                refreshKey={`${reservation.widget_request || ''}|${(reservation.equipment || []).join(',')}|${reservation.reservation_status || ''}`}
              />
            )}

            <DetailsCard>
              <DetailsCardHead>
                <DetailsCardTitle>{t('reservationDetails.equipment')}</DetailsCardTitle>
                {liczbaSztuk > 0 && <DetailsCount>{t('reservationDetails.itemsCount', { count: liczbaSztuk })}</DetailsCount>}
              </DetailsCardHead>
              {renderSprzetPodglad()}
            </DetailsCard>

            {/* Wydanie i zwrot per sztuka (migracja 045). Tylko w podglądzie: w edycji lista sprzętu
                jest w trakcie zmiany, a skan odhaczałby sztukę, która za chwilę może wypaść. */}
            <DetailsCard data-cy="reservation-handover-section">
              <DetailsCardHead>
                <DetailsCardTitle>{t('handover.title')}</DetailsCardTitle>
              </DetailsCardHead>
              <HandoverPanel reservationId={reservation.id} active={!isEditing} onChange={handleHandoverItems} />
              {kaucjaDoZwolnienia && (
                <Banner tone="info" compact style={{ marginTop: 12 }} data-cy="handover-deposit-hint">{t('reservationDetails.depositAfterReturn')}</Banner>
              )}
            </DetailsCard>

            {notatka && (
              <DetailsCard>
                <DetailsCardHead>
                  <DetailsCardTitle>{t('reservationDetails.note')}</DetailsCardTitle>
                </DetailsCardHead>
                <NoteText>{notatka}</NoteText>
              </DetailsCard>
            )}

            {renderDokumentacja()}
          </DetailsColumn>

          <DetailsColumn>
            <MoneyCard $paid={oplacona}>
              <MoneyLabel>{t('reservationDetails.remaining')}</MoneyLabel>
              <MoneyAmount $paid={oplacona} data-cy="details-remaining">{kwotaZl(remainingAfterOnline)}</MoneyAmount>
              {oplacona && <MoneyNote>{t('reservationDetails.paidInFull')}</MoneyNote>}
              {balancePaidGrosze > 0 && (
                <MoneyNote>{t('reservationDetails.paidExtra', { amount: kwotaZl(balancePaidGrosze / 100) })}</MoneyNote>
              )}
              {renderTabelaPodsumowania({ bezPozostalo: true })}
            </MoneyCard>

            <DetailsCard>
              <DetailsCardHead>
                <DetailsCardTitle>{t('reservationDetails.payments')}</DetailsCardTitle>
              </DetailsCardHead>
              <PaymentGroup>
                {/* Anulowana: bez wysyłania linków do zapłaty (klient dostałby mail za rezerwację, której
                    nie ma). Kaucja zostaje — blokadę na karcie trzeba móc zwolnić. */}
                {statusRezerwacji === 'cancelled'
                  ? <NoteText data-cy="details-cancelled-note">{t('reservationDetails.cancelledNote')}</NoteText>
                  : <div>{renderPlatnoscPodglad()}</div>}
                <div>{renderKaucjaPodglad()}</div>
              </PaymentGroup>
            </DetailsCard>
          </DetailsColumn>
        </DetailsGrid>
      </>
    );
  };

  return (
    <FormModal onClick={onClose}>
      <FormContainer ref={formContainerRef} onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose} title="Zamknij" data-cy="details-close">×</CloseButton>

        {isEditing && (
        <FormHeader>
          <FormTitle>
            <CalendarIcon />
            {isEditing ? `Edytuj rezerwację #${reservation.id}` : `Szczegóły rezerwacji #${reservation.id}`}
          </FormTitle>
          <FormSubtitle>
            {isEditing ? 'Wprowadź zmiany w rezerwacji' : 'Przegląd wszystkich informacji o rezerwacji'}
          </FormSubtitle>
        </FormHeader>
        )}

        {loadingData && <LoadingSpinner>Ładowanie danych do edycji...</LoadingSpinner>}

        {isEditing && conflicts.length > 0 && (
          <FormPanel data-cy="reservation-conflicts" style={{ borderColor: 'var(--danger)' }}>
            <FormPanelHeader>
              <FormPanelIcon $tone="danger">
                <AlertTriangleIcon />
              </FormPanelIcon>
              <FormPanelTitle>Konflikt sprzętu</FormPanelTitle>
            </FormPanelHeader>
            <div style={{ color: 'var(--danger)', fontWeight: 600, marginBottom: 12 }}>
              Wybrany sprzęt jest zajęty w poniższych rezerwacjach. Zmień termin lub usuń wskazane pozycje.
            </div>
            <ul style={{ margin: '0 0 8px 18px', color: 'var(--text)' }}>
              {conflicts.map((c, idx) => {
                // Sprawdzenie w trakcie edycji (check-conflicts) podaje numer i klienta tamtej
                // rezerwacji (`otherStart`/`otherEnd`), a 409 zapisu, jak w nowej rezerwacji, tylko
                // sztukę i termin (`start`/`end`, 13.10.2026).
                const from = formatConflictDate(c.otherStart ?? c.start);
                const to = formatConflictDate(c.otherEnd ?? c.end);
                return (
                  <li key={`${c.equipmentId}-${c.otherReservationId ?? ''}-${idx}`}>
                    <strong>{c.equipmentName || `ID ${c.equipmentId}`}</strong>
                    {c.otherReservationId ? <>, rezerwacja #{c.otherReservationId}</> : null}
                    {c.otherClient ? ` (${c.otherClient})` : ''}:
                    {` ${from} → ${to}`}
                  </li>
                );
              })}
            </ul>
          </FormPanel>
        )}

        {(saveError || amountsBanner) && (
          <FormPanel data-kpi-error="true" data-cy="reservation-save-error" style={{ borderColor: 'var(--danger)', background: 'var(--danger-bg)' }}>
            <FormPanelHeader>
              <FormPanelIcon $tone="danger">
                <AlertTriangleIcon />
              </FormPanelIcon>
              <FormPanelTitle>Nie można zapisać rezerwacji</FormPanelTitle>
            </FormPanelHeader>
            <div style={{ fontWeight: 600 }}>{saveError || amountsBanner}</div>
          </FormPanel>
        )}

        {!isEditing && renderPodglad()}

        {isEditing && (<>
        {/* Dane podstawowe */}
        <FormPanel>
          <FormPanelHeader>
            <FormPanelIcon $tone="steel"><UserIcon /></FormPanelIcon>
            <FormPanelTitle>Dane podstawowe</FormPanelTitle>
          </FormPanelHeader>
          <FormInputGroup>
            <FormInputWrapper>
              <FormLabel><UserIcon /> Imię*</FormLabel>
              {isEditing ? (
                <FormInput type="text" value={editForm.firstname} onChange={e => handleInputChange('firstname', e.target.value)} placeholder="Imię" required />
              ) : (
                <FormValue>{formatValue(reservation.firstname)}</FormValue>
              )}
            </FormInputWrapper>
            <FormInputWrapper>
              <FormLabel><UserIcon /> Nazwisko*</FormLabel>
              {isEditing ? (
                <FormInput type="text" value={editForm.lastname} onChange={e => handleInputChange('lastname', e.target.value)} placeholder="Nazwisko" required />
              ) : (
                <FormValue>{formatValue(reservation.lastname)}</FormValue>
              )}
            </FormInputWrapper>
          </FormInputGroup>
        </FormPanel>

        {/* Dane kontaktowe */}
        <FormPanel>
          <FormPanelHeader>
            <FormPanelIcon $tone="steel"><PhoneIcon /></FormPanelIcon>
            <FormPanelTitle>Dane kontaktowe</FormPanelTitle>
          </FormPanelHeader>
          <ContactGrid>
            <FormInputWrapper>
              {/* X.2: bez gwiazdek, bo klient ma e-mail ALBO telefon. */}
              <FormLabel><MailIcon /> Email</FormLabel>
              {isEditing ? (
                <FormInput type="email" value={editForm.email} onChange={e => handleInputChange('email', e.target.value)} placeholder="Email" />
              ) : (
                <FormValue className={!reservation.email ? 'empty' : ''}>{formatValue(reservation.email)}</FormValue>
              )}
            </FormInputWrapper>

            <FormInputWrapper>
              <FormLabel><PhoneIcon /> Telefon</FormLabel>
              {isEditing ? (
                <FormInput type="tel" value={editForm.phone} onChange={e => handleInputChange('phone', e.target.value)} placeholder="Telefon" />
              ) : (
                <FormValue className={!reservation.phone ? 'empty' : ''}>{formatValue(reservation.phone)}</FormValue>
              )}
            </FormInputWrapper>

            <FormInputWrapper>
              <FormLabel><MapPinIcon /> Adres</FormLabel>
              {isEditing ? (
                <>
                  <FormInput
                    type="text"
                    readOnly
                    value={(function(f){
                      const parts = [];
                      if (f.street) parts.push(f.street);
                      if (f.building_number) parts.push(f.apartment_number ? `${f.building_number}/${f.apartment_number}` : f.building_number);
                      const pcCity = [f.postal_code, f.city].filter(Boolean).join(' ');
                      if (pcCity) parts.push(pcCity);
                      return parts.length ? parts.join(', ') : '';
                    })(editForm)}
                    onClick={() => setShowAddressModal(true)}
                    placeholder="Kliknij, aby edytować adres"
                    aria-label="Adres"
                  />
                  <small style={{ display: 'block', marginTop: 6, color: 'var(--text-muted)', fontSize: '.85rem' }}>
                    Kliknij, aby edytować ulicę / nr / kod pocztowy / miasto
                  </small>
                </>
              ) : (
                <FormValue className={!reservation.street && !reservation.postal_code && !reservation.city ? 'empty' : ''}>
                  {(() => {
                    const parts = [];
                    if (reservation.street) parts.push(reservation.street);
                    if (reservation.building_number) parts.push(reservation.apartment_number ? `${reservation.building_number}/${reservation.apartment_number}` : reservation.building_number);
                    const pcCity = [reservation.postal_code, reservation.city].filter(Boolean).join(' ');
                    if (pcCity) parts.push(pcCity);
                    return parts.length ? parts.join(', ') : 'Brak danych';
                  })()}
                </FormValue>
              )}
            </FormInputWrapper>
          </ContactGrid>
        </FormPanel>

        {/* Termin rezerwacji + Płatność */}
        <CompactGrid>
          <FormPanel>
            <FormPanelHeader>
              <FormPanelIcon $tone="dusk"><CalendarIcon /></FormPanelIcon>
              <FormPanelTitle>Termin rezerwacji</FormPanelTitle>
            </FormPanelHeader>
            {isEditing ? (
              <DatepickerZIndexFix>
                <FormInputWrapper style={{maxWidth: '100%'}}>
                  <RangeLabelRow>
                    <FormLabel style={{marginBottom: 0}}><CalendarIcon /> Zakres dat*</FormLabel>
                    <ClearRangeButton
                      type="button"
                      onClick={() => { handleInputChange('start_date', null); handleInputChange('end_date', null); }}
                      disabled={false}
                      title="Wyczyść zakres dat"
                    >
                      Wyczyść
                    </ClearRangeButton>
                  </RangeLabelRow>
                  <DatePicker
                    popperModifiers={DOSUN_DO_EKRANU}
                    selected={editForm.start_date || null}
                    onChange={(dates) => {
                      const [start, end] = dates || [null, null];
                      handleInputChange('start_date', start || null);
                      handleInputChange('end_date', end || null);
                    }}
                    startDate={editForm.start_date || null}
                    endDate={editForm.end_date || null}
                    selectsRange
                    locale="pl"
                    minDate={getStartOfToday()}
                    filterDate={d => d >= getStartOfToday()}
                    dateFormat="yyyy-MM-dd"
                    shouldCloseOnSelect={false}
                    customInput={
                      <CustomRangeInput
                        start={editForm.start_date}
                        end={editForm.end_date}
                        placeholderStart="Od"
                        placeholderEnd="Do"
                      />
                    }
                    dayClassName={(date) => {
                      const classes = [];
                      const day = date.getDay();
                      if (day === 0 || day === 6) classes.push('weekend');
                      // Każdy dzień przed dzisiejszym, także z poprzedniego miesiąca widoczny na
                      // przełomie (1. dnia miesiąca wczoraj nie był wyszarzony, pomiar 28.09.2026).
                      if (date < getStartOfToday()) classes.push('past-day');
                      return classes.join(' ');
                    }}
                  />
                </FormInputWrapper>
              </DatepickerZIndexFix>
            ) : (
              <FormInputWrapper>
                <FormLabel><CalendarIcon /> Zakres dat*</FormLabel>
                <FormValue>{formatDate(reservation.start_date, { locale: currentLanguage })} → {formatDate(reservation.end_date, { locale: currentLanguage })}</FormValue>
              </FormInputWrapper>
            )}
          </FormPanel>

          <FormPanel>
              <FormPanelHeader>
                <FormPanelIcon $tone="gold"><TagIcon /></FormPanelIcon>
                <FormPanelTitle>Płatność</FormPanelTitle>
              </FormPanelHeader>
              {isEditing ? (
                <FormInputGroup>
                  {/* U.26: te same pola co w Nowej rezerwacji (ui/AmountField.js) — surowy tekst,
                      przecinek dziesiętny, nic nie jest przycinane w trakcie pisania. */}
                  <FormInputWrapper>
                    <FormLabel htmlFor="reservation-edit-discount"><TagIcon /> Rabat</FormLabel>
                    <div>
                      <DiscountField
                        id="reservation-edit-discount"
                        value={editForm.discount_value}
                        unit={editForm.discount_unit}
                        onChange={raw => setAmountField('discount_value', 'discount', 'discount', raw)}
                        onUnitChange={(nextUnit) => {
                          // Zmiana jednostki nie przelicza ani nie przycina wpisanej liczby; limit
                          // pokazujemy od razu, bo 150 w zł może być poprawne, a 150% już nie.
                          setEditForm(prev => ({ ...prev, discount_unit: nextUnit }));
                          showAmountCheck('discount');
                        }}
                        onBlur={() => showAmountCheck('discount')}
                        placeholder={discountUnit === 'PLN' ? t('amountField.discountPlaceholderPln') : t('amountField.discountPlaceholderPercent')}
                        invalid={!!discountLimitNote || !!fieldErrors.discount}
                        describedBy="reservation-edit-discount-note"
                        dataCy="reservation-edit-discount"
                      />
                      <AmountNote id="reservation-edit-discount-note" data-error={!!discountLimitNote || !!fieldErrors.discount} data-cy="reservation-edit-discount-note">
                        {discountLimitNote
                          || (!fieldErrors.discount && amountOrZero(editForm.discount_value) > 0 && equipmentPrice > 0
                            ? t('amountField.afterDiscount', { amount: formatMoney(pricing.total, { locale: currentLanguage }) })
                            : null)}
                      </AmountNote>
                    </div>
                  </FormInputWrapper>

                  <FormInputWrapper>
                    <FormLabel htmlFor="reservation-edit-advance"><CreditCardIcon /> Zaliczka</FormLabel>
                    <div>
                      <AmountField
                        id="reservation-edit-advance"
                        name="advance_amount"
                        value={editForm.advance_amount}
                        onChange={raw => setAmountField('advance_amount', 'advance', 'advanceAmount', raw)}
                        onBlur={() => showAmountCheck('advance')}
                        placeholder={t('amountField.advancePlaceholder')}
                        invalid={!!advanceLimitNote || !!fieldErrors.advanceAmount}
                        describedBy="reservation-edit-advance-note"
                        dataCy="reservation-edit-advance"
                      />
                      <AmountNote id="reservation-edit-advance-note" data-error={!!advanceLimitNote || !!fieldErrors.advanceAmount} data-cy="reservation-edit-advance-note">
                        {advanceLimitNote}
                      </AmountNote>

                      {/* P3.15: status opłacenia wynika z WPISÓW PŁATNOŚCI (nie ręczny checkbox).
                          Ustawiasz go opłacając zaliczkę (link/karta/gotówka/przelew), cofasz przez
                          „Cofnij wpis" na liście Płatności — jedno źródło prawdy. */}
                      <div style={{ marginTop: 6, fontSize: '.82rem', fontWeight: 600, color: reservation.advance_paid ? 'var(--success)' : 'var(--text-muted)' }}>
                        {reservation.advance_paid ? '✓ Zaliczka opłacona' : 'Zaliczka nieopłacona'}
                      </div>
                      {/* F.6: zapis zmienionych dat albo zaliczki zdejmuje termin wpłaty (serwer). */}
                      {terminWidoczny && (
                        <div style={{ marginTop: 4, fontSize: '.78rem', color: 'var(--text-muted)' }} data-cy="reservation-edit-deadline-note">
                          {t('paymentDeadline.editNote')}
                        </div>
                      )}
                    </div>
                  </FormInputWrapper>
                </FormInputGroup>
              ) : null}
            </FormPanel>
          </CompactGrid>

        {/* Wybrany sprzęt */}
        <FormPanel>
          <FormPanelHeader>
            <FormPanelIcon $tone="pine"><PackageIcon /></FormPanelIcon>
            <FormPanelTitle>Wybrany sprzęt*</FormPanelTitle>
            {isEditing && (
              <EquipmentButton
                type="button"
                enabled={true}
                onClick={async () => { await loadEditData(); setShowEquipmentModal(true); }}
                style={{ marginLeft: 'auto' }}
              >
                <PlusIcon /> Wybierz sprzęt
              </EquipmentButton>
            )}
          </FormPanelHeader>

          {isEditing ? (
            editForm.equipment_ids.length > 0 ? (
              <FormEquipmentList>
                {equipmentGroups.map((group, gi) => (
                  <BundleFrame key={group.bundle ? `bundle-${gi}` : `eq-${group.items[0].id}`} bundle={group.bundle}>
                    {group.items.map(equipment => (
                      <EquipmentItemRow key={equipment.id}>
                        <EquipmentInfo>
                          <PackageIcon />
                          <div>
                            <EquipmentName>{equipment.name}</EquipmentName>
                            <br />
                            <EquipmentMeta>
                              {equipment.inventory_number && `Nr: ${equipment.inventory_number} • `}{equipment.class} • {equipment.category} • Rozmiar: {equipment.size}
                            </EquipmentMeta>
                          </div>
                        </EquipmentInfo>
                       <EquipmentActions>
                          <div style={{ textAlign: 'right' }}>
                            <EquipmentPrice>
                              <ItemRate item={itemView(equipment)} baseText={`${kwotaZl(getEquipmentDailyRate(equipment, editingEquipmentLongTerm))}/dzień`} />
                            </EquipmentPrice>
                            <RuleBadges item={itemView(equipment)} />
                          </div>
                          <RemoveButton type="button" onClick={() => handleEquipmentToggle(equipment.id)} title="Usuń sprzęt">
                            <TrashIcon />
                          </RemoveButton>
                       </EquipmentActions>
                      </EquipmentItemRow>
                    ))}
                  </BundleFrame>
                ))}
              </FormEquipmentList>
            ) : (
              <EmptyState>
                <div className="icon"><PackageIcon /></div>
                <div className="text">Brak wybranego sprzętu - kliknij "Wybierz sprzęt"</div>
              </EmptyState>
            )
          ) : null}
        </FormPanel>

        {/* Notatki */}
        {isEditing ? (
          <FormPanel>
            <FormPanelHeader>
              <FormPanelIcon $tone="clay"><FileTextIcon /></FormPanelIcon>
              <FormPanelTitle>Notatki</FormPanelTitle>
            </FormPanelHeader>
            <FormInputWrapper>
              <FormTextarea
                value={editForm.additional_note || ''}
                onChange={e => handleInputChange('additional_note', e.target.value)}
                placeholder={reservation && reservation.additional_note ? '' : 'Wprowadź notatkę'}
              />
            </FormInputWrapper>
          </FormPanel>
        ) : null}
        </>)}

        {/* P5.2: Foto-dokumentacja stanu sprzętu (wydanie/zwrot) — U.9: widoczne też w edycji */}
        {isEditing && renderDokumentacja()}

        {/* Podsumowanie (w podglądzie: karta rozliczenia w renderPodglad) */}
        {isEditing && (<FormPanel>
          <FormPanelHeader>
            <FormPanelIcon $tone="gold"><CreditCardIcon /></FormPanelIcon>
            <FormPanelTitle>Podsumowanie</FormPanelTitle>
          </FormPanelHeader>
          {renderTabelaPodsumowania()}
        </FormPanel>)}

        {/* Anulowana rezerwacja w podglądzie nie ma akcji: edycja, umowa i faktura do niej nie pasują. */}
        {(isEditing || statusRezerwacji !== 'cancelled') && (
        <StickyActions>
          {/* F.11: w przerwie anulowanie, edycja i umowa są wyłączone (zwroty i kaucje zostają w karcie
              płatności). F.24: faktura działa także w przerwie, więc jej przycisk stoi poza blokadą. */}
          {!isEditing ? (
            <>
              <TylkoOdczytWPrzerwie>
              {statusRezerwacji === 'upcoming' && (
                <SecondaryButton
                  onClick={onDelete}
                  data-cy="reservation-cancel"
                  style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}
                >
                  {t('calendar.delete.confirmCancel')}
                </SecondaryButton>
              )}
              <span className="spacer" aria-hidden="true" />

              {/* N.6: umowa rezerwacji bez zapisanej kwoty czeka na cenę z wyceny (przeliczanie trwa chwilę). */}
              <SecondaryButton
                onClick={generateContractNow}
                disabled={generatingContract || podgladWycenaTrwa}
                aria-busy={generatingContract ? 'true' : undefined}
                data-cy="reservation-generate-contract"
              >
                <DocumentIcon />
                {generatingContract ? t('reservation.generatingContract') : t('reservation.generateContract', 'Generuj umowę')}
              </SecondaryButton>
              </TylkoOdczytWPrzerwie>

              <SecondaryButton data-cy="reservation-issue-invoice" onClick={() => navigate(`/invoices/new?reservation=${reservation.id}`)}>
                <DocumentIcon />
                Wystaw fakturę
              </SecondaryButton>

              <TylkoOdczytWPrzerwie>
              <PrimaryButton data-cy="reservation-edit" onClick={() => setIsEditing(true)}>
                Edytuj rezerwację
              </PrimaryButton>
              </TylkoOdczytWPrzerwie>
            </>
          ) : (
            <TylkoOdczytWPrzerwie>
              <SecondaryButton onClick={() => { setIsEditing(false); setSaveError(''); }} disabled={isSaving}>
                Anuluj
              </SecondaryButton>

              <SecondaryButton
                onClick={generateContractNow}
                disabled={generatingContract || isSaving || loadingData || (Array.isArray(editForm.equipment_ids) ? editForm.equipment_ids.length === 0 : false)}
                aria-busy={generatingContract ? 'true' : undefined}
              >
                <DocumentIcon />
                {generatingContract ? t('reservation.generatingContract') : t('reservation.generateContract', 'Generuj umowę')}
              </SecondaryButton>

              <PrimaryButton
                data-cy="reservation-edit-save"
                onClick={handleSave}
                disabled={
                  isSaving ||
                  loadingData ||
                  checkingConflicts ||
                  conflicts.length > 0 ||
                  (Array.isArray(editForm.equipment_ids) ? editForm.equipment_ids.length === 0 : false)
                }
              >
                {isSaving ? 'Zapisywanie...' : 'Zapisz zmiany'}
              </PrimaryButton>
            </TylkoOdczytWPrzerwie>
          )}
        </StickyActions>
        )}
      </FormContainer>

      {isEditing && showEquipmentModal && (
        <InventoryModal
          isOpen={showEquipmentModal}
          onClose={() => setShowEquipmentModal(false)}
          equipmentList={availableEquipment}
          selectedIds={editForm.equipment_ids}
          onToggle={(id) => handleEquipmentToggle(id)}
        />
      )}

      <AddressModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        values={editForm}
        onSave={(vals) => {
          handleInputChange('street', vals.street || '');
          handleInputChange('building_number', vals.building_number || '');
          handleInputChange('apartment_number', vals.apartment_number || '');
          handleInputChange('postal_code', vals.postal_code || '');
          handleInputChange('city', vals.city || '');
        }}
      />

      <DatepickerTheme />
    </FormModal>
  );
};

export default ReservationFormView;
